import Alpine from "alpinejs";

import {
  createLocaleController,
  formatDate as formatLocaleDate,
  formatNumber as formatLocaleNumber,
  plural as translatePlural,
  pluralForm as localePluralForm,
  resolveLocale,
  setActiveLocale,
  translate,
} from "./locale.js";

import {
  advanceSessionEpoch,
  apiFetch,
  captureSessionEpoch,
  captureSessionIdentity,
  changePassword as changePasswordCommand,
  currentSession,
  createOwner,
  fenceSessionLifetime,
  isSessionEpochCurrent,
  isSessionIdentityCurrent,
  login as createSession,
  logoutAll as destroyAllSessions,
  ownerBootstrapStatus,
  logout as destroySession,
  sendSessionActivity,
  setUnauthorizedHandler,
} from "./api.js";
import {
  clearReturnTarget,
  consumeReturnTarget,
  saveReturnTarget,
} from "./return-target.js";
import {
  ROUTE_SECTION,
  ROUTE_WIZARD,
  WIZARD_MODE_CREATE,
  WIZARD_MODE_EDIT,
  initialRouteState,
  isSafeFilter,
  isSafePage,
  parseRoute,
  parseRouteFromTarget,
  readHistoryState,
  routeHistoryState,
  routeSignature,
  sectionRoute,
  serializeRoute,
  wizardRoute,
} from "./router.js";
import {
  PASSWORD_CHANGED_NOTICE,
  SESSION_ENDED_NOTICE,
  commandFailureNotice,
} from "./system-session.js";
import {
  createActivityScheduler,
  shouldTrackActivityEvent,
  TRUSTED_ACTIVITY_EVENTS,
} from "./session-activity.js";

import {
  DOCUMENT_STATUS_ALL,
  documentCanRunRetrieval as canRunDocumentRetrieval,
  documentProcessingSteps,
  documentProcessingSummary,
  documentStatus,
  filterDocuments,
} from "./documents.js";
import {
  loadModelSettings as loadModelSettingsRequest,
  loadReadiness,
  modelActualDimension,
  modelDraft as emptyModelDraft,
  modelDraftFromSettings,
  modelProbeAccepted,
  modelReasonKey,
  modelRefusalReason,
  modelSavePayload,
  modelSettingsErrorKey,
  modelTestPayload,
  readinessRows,
  readinessStatus,
  saveModelSettings as saveModelSettingsRequest,
  storageActionKey,
  storageComponentKey,
  storageReasonKey,
  storageStatusKey,
  testModelSettings as testModelSettingsRequest,
  validateModelDraft,
} from "./model-settings.js";
import {
  answerEvaluationMethod as answerEvaluationMethodValue,
  answerEvaluationMethodLabel as answerEvaluationMethodLabelValue,
  answerMetric as answerMetricValue,
  addExpectedSource,
  bestExternalRagasRun as bestExternalRagasRunValue,
  canDownloadRagasExport as canDownloadRagasExportValue,
  communitySmokeEndpoint,
  communitySmokeRequestBody,
  evaluationDatasetCaseEndpoint,
  evaluationDatasetCasesEndpoint,
  evaluationDatasetEndpoint,
  evaluationDatasetRunEndpoint,
  evaluationDatasetsEndpoint,
  evaluationDocumentScopeLabel as evaluationDocumentScopeLabelValue,
  evaluationMetricDelta,
  evaluationMetricDeltaTone,
  evaluationRunDocumentIds,
  expectedSourceFromDocumentChunk,
  expectedSourceFromRetrievalResult,
  expectedSourceSummary as expectedSourceSummaryValue,
  externalRagasCaseMetric as externalRagasCaseMetricValue,
  externalRagasCaseMetricDelta as externalRagasCaseMetricDeltaValue,
  externalRagasCaseResult as externalRagasCaseResultValue,
  externalRagasMetric as externalRagasMetricValue,
  externalRagasMetricDelta as externalRagasMetricDeltaValue,
  externalRagasRunMetricDelta as externalRagasRunMetricDeltaValue,
  externalRagasRunEndpoint,
  externalRagasRunFileName,
  externalRagasRunJsonPayload as externalRagasRunJsonPayloadValue,
  externalRagasRunUsesCaseIds as externalRagasRunUsesCaseIdsValue,
  externalRagasRunsEndpoint,
  filterEvaluationRuns,
  filterExternalRagasRuns,
  formatExpectedSourcesJson,
  groupEvaluationRuns,
  hasAnswerMetrics as caseHasAnswerMetrics,
  normalizeExternalRagasImportPayload,
  parseExpectedSourcesJson,
  ragasExportEndpoint,
  ragasExportFileName,
  removeExpectedSource,
  runAggregateMetric,
  selectedExternalRagasRun as selectedExternalRagasRunValue,
  sortExternalRagasRuns,
} from "./evaluation.js";

const DOCUMENT_WIZARD_STORAGE_KEY = "freehub.documentWizard.v1";
const DOCUMENTS_REFRESH_MIN_DELAY_MS = 2_000;
const DOCUMENTS_REFRESH_MAX_DELAY_MS = 10_000;
// Story 1.14: production-deadline проверки готовности на landing. Backend
// проверяет postgres/qdrant/graph/s3 последовательно с CHECK_TIMEOUT=5s, т.е.
// штатный worst-case ~20s; deadline обязан быть больше, иначе медленный, но
// валидный 503 превращался бы в «процесс недоступен» и терял reason/action.
const OVERVIEW_READINESS_TIMEOUT_MS = 22_000;
// Размер файла показывается с одним знаком после запятой/точки — сам разделитель
// даёт Intl текущего locale.
const BYTES_FRACTION_OPTIONS = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

// Story 1.7 / FR-4: единственная storage-граница для locale preference.
// Недоступное/запрещённое storage не ломает UI: контроллер живёт в памяти
// текущего runtime без обещания persistence через reload.
function localeStorageBoundary() {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    return typeof storage.getItem === "function" && typeof storage.setItem === "function"
      ? storage
      : null;
  } catch (_error) {
    return null;
  }
}

const localeController = createLocaleController({ storage: localeStorageBoundary() });

/** Locale-ключ по семантическому коду api-ошибки; иначе безопасный fallback-ключ. */
function errorLocaleKey(error, fallbackKey) {
  const key = error && typeof error.localeKey === "string" ? error.localeKey : "";
  return key || fallbackKey;
}

window.freehubShell = function freehubShell() {
  return {
    // Story 1.7: активный locale оболочки (en/ru) — реактивное поле, поэтому
    // `t()` в разметке перерисовывается без remount формы/приложения.
    locale: localeController.locale,
    authChecking: true,
    authFlowVersion: 0,
    sessionNotice: "",
    loggingOut: false,
    // System-панель Story 1.6: отдельные состояния для end-all и смены пароля.
    systemBusy: false,
    systemError: "",
    systemAllSessionsConfirm: false,
    passwordChangeOpen: false,
    passwordChangeBusy: false,
    passwordChangeError: "",
    passwordChangeCurrent: "",
    passwordChangeNew: "",
    passwordChangeConfirmation: "",
    // Story 1.10: model settings + read-only storage diagnostics. Credential
    // живёт только в input property и не сохраняется в состоянии после ответа.
    modelSettingsLoading: false,
    modelSettingsError: "",
    modelConfigured: false,
    modelRevision: 0,
    modelProvider: "local",
    modelBaseUrl: "",
    modelName: "",
    modelDeclaredDimension: "",
    modelCredential: "",
    modelCredentialPresent: false,
    modelTesting: false,
    modelSaving: false,
    modelTestStatus: "idle",
    modelTestRequired: false,
    modelTestDimension: null,
    modelTestReason: "",
    modelTestModel: "",
    modelProbeId: "",
    modelDraftToken: 0,
    modelTestRequest: 0,
    modelSaved: false,
    storageDiagnosticsStatus: "unknown",
    storageDiagnosticsRows: [],
    storageDiagnosticsError: "",
    // Story 1.14: правдивое состояние системы на landing. `overviewStatus` —
    // готовность служб (ready/not_ready/unknown/stale), `overviewProcessHealth`
    // — ответил ли процесс API; это разные факты. Первое действие upload
    // открывается только при подтверждённом ready.
    overviewStatus: "unknown",
    overviewProcessHealth: "unavailable",
    overviewChecks: [],
    overviewPending: false,
    overviewRequest: 0,
    // R3b: какой wizard-вход владеет текущей проверкой readiness. Поздний ответ
    // прежнего входа (уход на раздел/новый вход) не открывает мастер.
    wizardEntryToken: 0,
    // Story 1.14 / R4: факт когда-либо подтверждённого контракта. Второй подряд
    // сбой не должен снимать явную stale-маркировку до recovery/reset сессии.
    overviewHasContract: false,
    activityScheduler: null,
    activityListener: null,
    activityEvents: [],
    authenticated: false,
    authUsername: "",
    bootstrapFlowVersion: 0,
    loginUsername: "",
    loginPassword: "",
    loginBusy: false,
    loginError: "",
    bootstrapInitStarted: false,
    bootstrapStatus: "checking",
    bootstrapCheckError: "",
    bootstrapUsername: "",
    bootstrapPassword: "",
    bootstrapPasswordConfirmation: "",
    bootstrapBusy: false,
    bootstrapError: "",
    section: "documents",
    navOpen: false,
    // AC-4: локальный ключ подтверждения копирования (не пользовательский текст).
    copyNotice: "",
    // Story 1.8: единственный route-owner поверх прежнего state-owner.
    // `routeNotice`/`routeError` — безопасные locale-ключи, не пользовательский текст;
    // `pendingRoute` — уже валидированный return target, применяемый ровно один раз.
    routeNotice: "",
    routeError: "",
    pendingRoute: null,
    appliedRouteSignature: "",
    routePopStateListener: null,
    documents: [],
    documentsLoading: false,
    documentsError: "",
    documentsRefreshTimer: null,
    documentsRefreshAttempts: 0,
    documentsRefreshSignature: "",
    documentSearch: "",
    documentStatusFilter: DOCUMENT_STATUS_ALL,
    documentsPage: 1,
    documentsPageSize: 4,
    documentStatusFilters: [
      { value: DOCUMENT_STATUS_ALL, label: "Все" },
      { value: "completed", label: "Готово" },
      { value: "processing", label: "В работе" },
      { value: "failed", label: "Ошибка" },
      { value: "registered", label: "Новые" },
    ],
    selectedDocumentId: "",
    selectedDocumentTitle: "",
    documentChunks: [],
    documentChunksLoading: false,
    documentChunksError: "",
    selectedNormalizedDocumentId: "",
    selectedNormalizedDocumentTitle: "",
    normalizedDocument: null,
    normalizedObjectKey: "",
    normalizedDocumentLoading: false,
    normalizedDocumentError: "",
    uploadOpen: false,
    documentWizardMode: "create",
    documentWizardEditId: "",
    documentWizardInitialTitle: "",
    documentWizardStep: 1,
    // Story 1.8/F1: какому route/entity принадлежит in-memory draft мастера.
    documentWizardDraftKey: "",
    // Story 1.8/F4: identity операции этого draft. Пока ответ PATCH в пути,
    // пользователь может уйти Back-ом/закрыть мастер/открыть другой объект;
    // такой ответ видит чужой token и не публикует свои поля, шаг и storage.
    documentWizardDraftToken: 0,
    uploadBusy: false,
    uploadError: "",
    uploadTitle: "",
    uploadFile: null,
    uploadWizardDocumentId: "",
    uploadWizardDocumentTitle: "",
    reprocessBusyId: "",
    deleteBusyId: "",
    retrievalQuery: "",
    retrievalBusy: false,
    retrievalAnswerBusy: false,
    retrievalError: "",
    retrievalResults: [],
    retrievalProfile: "hybrid_balanced",
    retrievalAppliedProfile: "",
    retrievalEngineProfile: "",
    retrievalAnswerProfile: "",
    retrievalAnswer: "",
    retrievalAnswerCitations: [],
    retrievalRuns: [],
    retrievalRunsLoading: false,
    retrievalRunsError: "",
    evaluationRuns: [],
    evaluationRunsLoading: false,
    evaluationRunsError: "",
    evaluationRunBusy: false,
    evaluationStatusFilter: "all",
    evaluationProfileFilter: "all",
    evaluationStatusFilters: [
      { value: "all", label: "Все статусы" },
      { value: "running", label: "Выполняется" },
      { value: "completed", label: "Готов" },
      { value: "failed", label: "Ошибка" },
    ],
    evaluationDocumentIds: [],
    evaluationExportBusyId: "",
    evaluationCompareBaseId: "",
    evaluationCompareCandidateId: "",
    selectedEvaluationRunId: "",
    selectedEvaluationRunName: "",
    evaluationCases: [],
    evaluationCasesLoading: false,
    evaluationCasesError: "",
    evaluationDatasets: [],
    evaluationDatasetsLoading: false,
    evaluationDatasetsError: "",
    selectedEvaluationDatasetId: "",
    evaluationDatasetCases: [],
    evaluationDatasetCasesLoading: false,
    evaluationDatasetCasesError: "",
    evaluationDatasetCreateBusy: false,
    evaluationDatasetUpdateBusy: false,
    evaluationDatasetDeleteBusyId: "",
    evaluationDatasetCaseCreateBusy: false,
    evaluationDatasetCaseDeleteBusyId: "",
    editingEvaluationCaseId: "",
    evaluationDatasetName: "",
    evaluationDatasetVersion: "v1",
    evaluationDatasetEditName: "",
    evaluationDatasetEditVersion: "",
    evaluationCaseQuestion: "",
    evaluationCaseReferenceText: "",
    evaluationCaseExpectedSources: [],
    evaluationCaseExpectedSourcesJson: "",
    evaluationCaseMustInclude: "",
    evaluationCaseMustNotInclude: "",
    evaluationCaseMaxResults: 8,
    evaluationCaseProfile: "hybrid_balanced",
    externalRagasRuns: [],
    externalRagasRunsLoading: false,
    externalRagasRunsError: "",
    externalRagasImportBusy: false,
    externalRagasJsonRunId: "",
    externalRagasDeleteBusyId: "",
    externalRagasFilter: "",
    externalRagasSort: "date_desc",
    externalRagasBestMetric: "faithfulness",
    selectedExternalRagasRunId: "",
    externalRagasCompareBaseId: "",
    externalRagasCompareCandidateId: "",
    externalRagasSortOptions: [
      { value: "date_desc", label: "Сначала новые" },
      { value: "date_asc", label: "Сначала старые" },
      { value: "answer_relevancy_desc", label: "Релевантность" },
      { value: "faithfulness_desc", label: "Опора" },
      { value: "context_precision_desc", label: "Precision" },
      { value: "context_recall_desc", label: "Recall" },
    ],
    externalRagasBestMetricOptions: [
      { value: "answer_relevancy", label: "Релевантность" },
      { value: "faithfulness", label: "Опора" },
      { value: "context_precision", label: "Precision" },
      { value: "context_recall", label: "Recall" },
    ],
    agents: [],
    agentsLoading: false,
    agentsError: "",
    agentCreateOpen: false,
    agentCreateBusy: false,
    agentCreateError: "",
    agentName: "",
    agentDescription: "",
    agentRetrievalProfile: "hybrid_balanced",
    selectedAgentDocumentsId: "",
    selectedAgentDocumentsName: "",
    agentDocumentIds: [],
    agentDocumentsLoading: false,
    agentDocumentsSaving: false,
    agentDocumentsError: "",
    agentProfileBusyId: "",
    agentProfileError: "",
    selectedAgentTestId: "",
    selectedAgentTestName: "",
    agentTestProfile: "",
    agentTestDocumentIds: [],
    agentTestQuery: "",
    agentTestResults: [],
    agentTestBusy: false,
    agentTestLoading: false,
    agentTestError: "",
    logs: [],
    logsLoading: false,
    logsError: "",
    logEventType: "",
    importJobs: [],
    importJobsLoading: false,
    importJobsError: "",
    retrievalProfiles: [
      { value: "hybrid_balanced", label: "Hybrid balanced" },
      { value: "llm_precise", label: "LLM precise" },
    ],
    // Story 1.7 / FR-4: минимальный wiring единственного locale owner.
    // `t()` читает реактивный `this.locale`, поэтому метки, ARIA, placeholders и
    // notices рендерятся в текущем языке и меняются без remount формы/app.
    t(key, variables) {
      return translate(this.locale, key, variables);
    },
    /** Смена языка: preference сохраняется, введённые значения не трогаются. */
    setLocale(value) {
      this.applyLocale(localeController.setLocale(value));
      return this.locale;
    },
    applyLocale(value) {
      this.locale = resolveLocale(value);
      setActiveLocale(this.locale);
      if (typeof document !== "undefined" && document.documentElement) {
        document.documentElement.lang = this.locale;
      }
    },
    formatNumber(value, options) {
      return formatLocaleNumber(this.locale, value, options);
    },
    plural(key, count, variables) {
      return translatePlural(this.locale, key, count, variables);
    },
    /** CLDR-категория числа текущего locale (`Intl.PluralRules`). */
    pluralForm(count) {
      return localePluralForm(this.locale, count);
    },
    async init() {
      setUnauthorizedHandler(() => this.handleUnauthorized());
      // Story 1.8: popstate обслуживает тот же router; listener ставится один раз
      // на lifetime страницы, а обработчик сам ограничен authenticated-сессией.
      this.installRouteListener();
      this.applyLocale(this.locale);
      if (!this.bootstrapInitStarted) {
        this.bootstrapInitStarted = true;
        this.checkOwnerBootstrap();
      }
      if (await this.restoreAuthSession()) this.startAuthenticatedUi();
    },
    async checkOwnerBootstrap() {
      const flowVersion = ++this.bootstrapFlowVersion;
      try {
        const status = await ownerBootstrapStatus();
        if (flowVersion !== this.bootstrapFlowVersion) return;
        this.bootstrapStatus = status;
        this.bootstrapCheckError = "";
        if (status === "required" && !this.authenticated) {
          this.$nextTick(() => this.$refs.bootstrapUsername?.focus());
        }
      } catch (error) {
        if (flowVersion !== this.bootstrapFlowVersion) return;
        this.bootstrapStatus = "error";
        this.bootstrapCheckError = errorLocaleKey(error, "error.auth_bootstrap_check_failed");
      }
    },
    async submitOwnerBootstrap() {
      if (this.bootstrapBusy) return;
      this.bootstrapError = "";
      if (this.bootstrapPassword !== this.bootstrapPasswordConfirmation) {
        this.bootstrapError = "error.bootstrap_password.mismatch";
        this.$nextTick(() => this.$refs.bootstrapPasswordConfirmation?.focus());
        return;
      }

      const flowVersion = ++this.bootstrapFlowVersion;
      this.bootstrapBusy = true;
      const username = this.bootstrapUsername.trim();
      try {
        const result = await createOwner(username, this.bootstrapPassword);
        if (flowVersion !== this.bootstrapFlowVersion) return;
        this.loginUsername = username;
        this.bootstrapPassword = "";
        this.bootstrapPasswordConfirmation = "";
        if (result.conflict) {
          this.bootstrapStatus = "error";
          this.loginError = "";
          try {
            const status = await ownerBootstrapStatus();
            if (flowVersion !== this.bootstrapFlowVersion) return;
            if (status === "complete") {
              this.bootstrapStatus = "complete";
              this.bootstrapCheckError = "";
              this.loginError = "notice.bootstrapAlreadyComplete";
            } else {
              this.bootstrapStatus = "error";
              this.bootstrapCheckError = "notice.bootstrapNotConfirmed";
            }
          } catch (error) {
            if (flowVersion !== this.bootstrapFlowVersion) return;
            this.bootstrapStatus = "error";
            this.bootstrapCheckError = errorLocaleKey(error, "error.auth_bootstrap_check_unavailable");
          }
        } else {
          this.bootstrapStatus = "complete";
          this.bootstrapCheckError = "";
          this.loginError = "notice.bootstrapOwnerCreated";
        }
        this.$nextTick(() => this.$refs.loginUsername?.focus());
      } catch (error) {
        if (flowVersion === this.bootstrapFlowVersion) {
          this.bootstrapError = errorLocaleKey(error, "error.auth_owner_create_failed");
        }
      } finally {
        this.bootstrapBusy = false;
      }
    },
    async restoreAuthSession() {
      const flowVersion = ++this.authFlowVersion;
      this.authChecking = true;
      try {
        const session = await currentSession();
        if (flowVersion !== this.authFlowVersion) return false;
        this.authenticated = Boolean(session.authenticated);
        this.authUsername = session.username || "";
        if (this.authenticated) {
          // Новая восстановленная session — отдельный epoch: late 401 прежней
          // анонимной проверки не должен её завершать.
          advanceSessionEpoch();
          this.sessionNotice = "";
        }
        if (this.authenticated && window.location.pathname === "/login") {
          // Story 1.8: восстановленная сессия не оставляет auth surface в адресе —
          // canonical документы заменяют `/login` без новой записи истории.
          window.history.replaceState(
            routeHistoryState(sectionRoute("documents"), {}),
            "",
            serializeRoute(sectionRoute("documents")),
          );
        } else if (!this.authenticated && window.location.pathname !== "/login") {
          // AC-3: «пользователь хотел раздел X без активной сессии» фиксируется
          // единственной точкой `saveReturnTarget`; после входа target
          // потребляется ровно один раз, а подтверждённый выход его удаляет.
          const target = this.pendingReturnTargetFromLocation();
          if (target !== null) saveReturnTarget(target);
          window.history.replaceState({}, "", "/login");
        }
        return this.authenticated;
      } catch (_error) {
        if (flowVersion !== this.authFlowVersion) return false;
        this.authenticated = false;
        return false;
      } finally {
        if (flowVersion === this.authFlowVersion) this.authChecking = false;
      }
    },
    async submitLogin() {
      if (this.loginBusy) return;
      const flowVersion = ++this.authFlowVersion;
      this.authChecking = false;
      this.loginBusy = true;
      this.loginError = "";
      try {
        const session = await createSession(this.loginUsername.trim(), this.loginPassword);
        if (flowVersion !== this.authFlowVersion) return;
        this.authenticated = true;
        this.authUsername = session.username || this.loginUsername.trim();
        this.loginPassword = "";
        this.sessionNotice = "";
        advanceSessionEpoch();
        // Потребление safe return target ровно один раз; невалидный/чужой
        // target уже удалён валидатором и даёт `/`.
        //
        // Story 1.8: возврат применяется тем же router-ом, а не только сменой
        // address bar — валидная цель разбирается в один canonical route, который
        // затем применяется ровно один раз в `startAuthenticatedUi`.
        this.pendingRoute = parseRouteFromTarget(consumeReturnTarget());
        window.history.replaceState(
          routeHistoryState(this.pendingRoute, {}),
          "",
          serializeRoute(this.pendingRoute),
        );
        this.startAuthenticatedUi();
      } catch (error) {
        if (flowVersion !== this.authFlowVersion) return;
        this.loginError = errorLocaleKey(error, "error.auth_login_failed");
      } finally {
        if (flowVersion === this.authFlowVersion) this.loginBusy = false;
      }
    },
    async logout() {
      if (this.loggingOut) return;
      this.loggingOut = true;
      this.authFlowVersion += 1;
      advanceSessionEpoch();
      let confirmed = false;
      try {
        const result = await destroySession();
        confirmed = result?.confirmed === true;
      } catch (_error) {
        confirmed = false;
      } finally {
        // Потребители, начатые в промежутке между началом logout (epoch уже
        // увеличен) и завершением серверного ответа, захватили промежуточный
        // epoch; завершение logout обязано инвалидировать и их, иначе поздний
        // разрешённый read воскрешает данные и заново планирует опрос после
        // очистки состояния.
        advanceSessionEpoch();
        this.clearSessionScopedState();
        // 503/сетевой сбой не выдаём за подтверждённый server revoke.
        this.sessionNotice = confirmed ? "" : "notice.logoutUnconfirmed";
        window.history.replaceState({}, "", "/login");
        this.loggingOut = false;
      }
    },
    // Централизованный отказ 401 текущего epoch (expiry/revoke): прекращает
    // polling/activity, очищает session-scoped UI и объясняет причину.
    handleUnauthorized() {
      if (this.loggingOut || !this.authenticated) return;
      this.endSession();
    },
    // Story 1.6 / CAP-1: завершение ВСЕХ административных сессий, включая эту.
    // `confirmed` выставляется только по подтверждённому серверному ответу;
    // 401/403/415/422/429/503, сетевой сбой и abort не выдаются за успех, а
    // ambiguous-исход не превращается ни в success, ни в обещание отката.
    async logoutAll() {
      if (this.systemBusy) return;
      this.systemBusy = true;
      this.systemError = "";
      // R1: старт команды НЕ двигает epoch сессии. Владелец результата — сессия,
      // а не «последняя запущенная security-команда»: иначе вторая команда той же
      // сессии сделала бы уже подтверждённый сервером успех первой stale, и
      // подтверждённый revoke терялся бы без logout/нового входа.
      const epoch = captureSessionEpoch();
      // C1: владелец операции — та же СЕССИЯ (identity), а не её lifetime.
      // Истечение/residual 401 ТОЙ ЖЕ сессии фехтует epoch, но не отменяет уже
      // подтверждённый сервером результат; новый вход меняет identity и делает
      // поздний ответ прежней команды чужим для новой сессии.
      const identity = captureSessionIdentity();
      // Владелец busy-флага: снимаем его только пока его не перехватила другая
      // security-команда (у каждой свой identity).
      this.systemBusyIdentity = identity;
      let confirmed = false;
      let code = "";
      try {
        const result = await destroyAllSessions();
        confirmed = result?.confirmed === true;
        code = result?.code || "";
      } catch (_error) {
        confirmed = false;
        code = "network";
      } finally {
        // Подтверждённый результат команды ЭТОЙ сессии публикуется даже если её
        // lifetime успел завершиться фоновым 401/expiry (C1). Неподтверждённый
        // исход при уже завершённом lifetime не публикуется вовсе: fail-closed —
        // истечение не скрывается и не переписывается сообщением команды.
        const sameSession = isSessionIdentityCurrent(identity);
        if (sameSession && (confirmed || isSessionEpochCurrent(epoch))) {
          if (confirmed) {
            // Подтверждённый end-all завершает сессию: только здесь epoch растёт
            // и закрывает потребителей, стартовавших во время команды, чтобы
            // поздний read не воскресил данные и не запланировал опрос после
            // очистки.
            advanceSessionEpoch();
            this.clearSessionScopedState();
            this.sessionNotice = SESSION_ENDED_NOTICE;
            window.history.replaceState({}, "", "/login");
          } else {
            this.systemAllSessionsConfirm = false;
            this.systemError = commandFailureNotice(code, "end-all");
          }
          this.systemBusy = false;
        } else if (this.systemBusyIdentity === identity) {
          // Поздний ответ прежней сессии: состояние, epoch, notices и inputs
          // новой сессии не изменяются. Снимается только собственный busy-флаг и
          // только если его не перехватила другая security-команда — иначе панель
          // осталась бы «занятой» навсегда.
          this.systemBusy = false;
        }
      }
    },
    // Story 1.6 / CAP-2: смена пароля. Подтверждение нового пароля — только UI;
    // серверу отправляются ровно current_password/new_password.
    async submitPasswordChange() {
      if (this.passwordChangeBusy) return;
      this.passwordChangeError = "";
      if (this.passwordChangeNew !== this.passwordChangeConfirmation) {
        this.passwordChangeError = "error.password.mismatch";
        return;
      }
      const currentPassword = this.passwordChangeCurrent;
      const newPassword = this.passwordChangeNew;
      this.passwordChangeBusy = true;
      // R1: как и у end-all, старт команды не двигает epoch сессии. Иначе
      // параллельная security-команда той же сессии сделала бы подтверждённый
      // успех этой команды stale, и завершение всех сессий/смена пароля потерялись
      // бы без logout/нового входа.
      const epoch = captureSessionEpoch();
      // C1: владелец результата — та же СЕССИЯ (identity), а не её lifetime:
      // фоновый 401 той же сессии не отменяет подтверждённую смену пароля, а
      // новый вход делает поздний ответ прежней команды чужим для новой сессии.
      const identity = captureSessionIdentity();
      this.passwordChangeBusyIdentity = identity;
      let changed = false;
      let code = "";
      try {
        const result = await changePasswordCommand(currentPassword, newPassword);
        changed = result?.changed === true;
        code = result?.code || "";
      } catch (_error) {
        changed = false;
        code = "network";
      } finally {
        // Подтверждённая смена пароля публикуется даже после фонового 401 той же
        // сессии (raw secrets при этом очищаются в обоих случаях). При уже
        // завершённом lifetime неподтверждённый исход не публикуется: fail-closed,
        // истечение не скрывается.
        const sameSession = isSessionIdentityCurrent(identity);
        if (sameSession && (changed || isSessionEpochCurrent(epoch))) {
          // Raw secrets очищаются и при успехе, и при отказе.
          this.clearPasswordChangeInputs();
          if (changed) {
            // Подтверждённая смена пароля завершает сессию: только здесь epoch
            // растёт (см. комментарий в `logoutAll`).
            advanceSessionEpoch();
            this.passwordChangeOpen = false;
            this.clearSessionScopedState();
            this.sessionNotice = PASSWORD_CHANGED_NOTICE;
            window.history.replaceState({}, "", "/login");
          } else {
            this.passwordChangeError = commandFailureNotice(code, "password-change");
          }
          this.passwordChangeBusy = false;
        } else if (this.passwordChangeBusyIdentity === identity) {
          // Поздний ответ прежней сессии: inputs/notices/epoch новой сессии не
          // трогаются; снимается только собственный busy-флаг, если его не
          // перехватила другая security-команда.
          this.passwordChangeBusy = false;
        }
      }
    },
    clearPasswordChangeInputs() {
      this.passwordChangeCurrent = "";
      this.passwordChangeNew = "";
      this.passwordChangeConfirmation = "";
    },
    closePasswordChangeForm() {
      this.passwordChangeOpen = false;
      this.passwordChangeError = "";
      this.clearPasswordChangeInputs();
    },
    // --- Story 1.10: model settings / read-only storage diagnostics --------
    /** Draft из текущих полей; credential остаётся только в input property. */
    modelDraftState() {
      return emptyModelDraft({
        provider: this.modelProvider,
        base_url: this.modelBaseUrl,
        model: this.modelName,
        declared_dimension: this.modelDeclaredDimension,
        credential: this.modelCredential,
      });
    },
    /**
     * Любое изменение draft сбрасывает успешный probe (и pending save): сохранён
     * может быть только ровно проверенный draft. Token инвалидирует поздние
     * async-ответы прежнего draft, а request-id освобождает pending-флаг именно
     * того запроса, даже если его результат уже не публикуется.
     */
    invalidateModelProbe() {
      this.modelDraftToken = (Number(this.modelDraftToken) || 0) + 1;
      this.modelTestRequest = (Number(this.modelTestRequest) || 0) + 1;
      this.modelTesting = false;
      this.modelProbeId = "";
      this.modelTestStatus = "idle";
      this.modelTestDimension = null;
      this.modelTestReason = "";
      this.modelTestModel = "";
      this.modelTestRequired = false;
      this.modelSaved = false;
    },
    /**
     * Применяет принятые сервером settings. `draftToken` — token, снятый в
     * начале read: если пользователь начал вводить новый draft, поздний GET
     * обновляет только серверные факты (revision/presence), но не затирает
     * введённые поля и не сбрасывает их probe.
     */
    applyModelSettings(settings, draftToken) {
      const payload = settings && typeof settings === "object" ? settings : {};
      const draftIsStale = draftToken !== undefined && draftToken !== this.modelDraftToken;
      this.modelConfigured = payload.configured === true;
      this.modelRevision = Number.isInteger(payload.revision) ? payload.revision : 0;
      this.modelCredentialPresent = payload.credential_present === true;
      if (draftIsStale) return false;
      const draft = modelDraftFromSettings(payload);
      this.modelProvider = draft.provider;
      this.modelBaseUrl = draft.base_url;
      this.modelName = draft.model;
      this.modelDeclaredDimension = draft.declared_dimension;
      // Write-only: введённое значение никогда не восстанавливается из readback.
      this.modelCredential = "";
      this.invalidateModelProbe();
      this.modelSettingsError = "";
      return true;
    },
    async loadModelSettingsPanel() {
      const epoch = captureSessionEpoch();
      const identity = captureSessionIdentity();
      const draftToken = this.modelDraftToken;
      this.modelSettingsLoading = true;
      this.modelSettingsError = "";
      try {
        const outcome = await loadModelSettingsRequest();
        if (!isSessionIdentityCurrent(identity) || !isSessionEpochCurrent(epoch)) return;
        if (!outcome.ok) {
          this.modelSettingsError = modelSettingsErrorKey(outcome);
          return;
        }
        this.applyModelSettings(outcome.payload, draftToken);
      } catch (_error) {
        if (isSessionIdentityCurrent(identity) && isSessionEpochCurrent(epoch)) {
          this.modelSettingsError = "error.model.unavailable";
        }
      } finally {
        if (isSessionIdentityCurrent(identity) && isSessionEpochCurrent(epoch)) {
          this.modelSettingsLoading = false;
        }
      }
    },
    async loadStorageDiagnostics() {
      const epoch = captureSessionEpoch();
      const identity = captureSessionIdentity();
      try {
        const outcome = await loadReadiness();
        if (!isSessionIdentityCurrent(identity) || !isSessionEpochCurrent(epoch)) return;
        // 503 — валидный ReadinessContract('not_ready'), не сетевой сбой.
        const contract = outcome.payload;
        this.storageDiagnosticsStatus = readinessStatus(contract);
        this.storageDiagnosticsRows = readinessRows(contract);
        this.storageDiagnosticsError =
          this.storageDiagnosticsStatus === "unknown" ? "error.model.unavailable" : "";
      } catch (_error) {
        if (isSessionIdentityCurrent(identity) && isSessionEpochCurrent(epoch)) {
          this.storageDiagnosticsStatus = "unknown";
          this.storageDiagnosticsRows = [];
          this.storageDiagnosticsError = "error.model.unavailable";
        }
      }
    },
    /**
     * Story 1.14: проверка готовности для landing-карточки.
     *
     * Валидный ReadinessContract (200 ready или 503 not_ready) доказывает, что
     * процесс API ответил: process health = "available", готовность служб
     * берётся из контракта. Timeout/сеть = процесс недоступен. Неразобранное
     * тело (HTML, мусор) = "unknown" — никогда не ready.
     *
     * `overviewRequest` + существующие session guards: поздний ответ прежней
     * проверки/сессии не перезаписывает новую и не снимает её pending.
     */
    async loadOverviewStatus() {
      const epoch = captureSessionEpoch();
      const identity = captureSessionIdentity();
      const request = (Number(this.overviewRequest) || 0) + 1;
      this.overviewRequest = request;
      this.overviewPending = true;
      const controller = new AbortController();
      // Bounded pending: без deadline зависший upstream оставил бы вечный spinner.
      const deadline = setTimeout(() => controller.abort(), OVERVIEW_READINESS_TIMEOUT_MS);
      try {
        const outcome = await loadReadiness({ signal: controller.signal });
        if (request !== this.overviewRequest) return;
        if (!isSessionIdentityCurrent(identity) || !isSessionEpochCurrent(epoch)) return;
        // 503 — валидный контракт ('not_ready'), не сетевой сбой. HTTP-код и
        // aggregate обязаны согласоваться: 200+not_ready и 503+ready — тоже
        // противоречивый ответ, а не доказанная готовность.
        const contractStatus = readinessStatus(outcome.payload);
        const status =
          (outcome.status === 200 && contractStatus === "ready") ||
          (outcome.status === 503 && contractStatus === "not_ready")
            ? contractStatus
            : "unknown";
        if (status === "unknown") {
          // Malformed/противоречивый ответ доказывает, что процесс ответил, но не
          // годность контракта: process = unknown, не «недоступен».
          this.applyOverviewFailure("unknown");
        } else {
          this.overviewHasContract = true;
          this.overviewProcessHealth = "available";
          this.overviewStatus = status;
          this.overviewChecks = readinessRows(outcome.payload);
        }
      } catch (_error) {
        if (request !== this.overviewRequest) return;
        if (!isSessionIdentityCurrent(identity) || !isSessionEpochCurrent(epoch)) return;
        this.applyOverviewFailure();
      } finally {
        clearTimeout(deadline);
        if (
          request === this.overviewRequest &&
          isSessionIdentityCurrent(identity) &&
          isSessionEpochCurrent(epoch)
        ) {
          this.overviewPending = false;
        }
      }
    },
    /**
     * Неподтверждённое состояние. `processHealth`: transport/timeout — процесс
     * недоступен (unavailable), malformed/противоречивый ответ — unknown (ответ
     * был, контракт не подтверждён). Службы — stale (был валидный контракт, теперь
     * проверить не удалось) либо unknown. Прежние строки не остаются со статусом
     * ready: данные устарели явно.
     */
    applyOverviewFailure(processHealth = "unavailable") {
      // R4: stale — свойство данных, а не одного перехода. Повторный отказ при
      // том же исходе сохраняет явную устарелость (и не переозвучивает её).
      const hadContract = this.overviewHasContract === true;
      this.overviewProcessHealth = processHealth === "unknown" ? "unknown" : "unavailable";
      this.overviewStatus = hadContract ? "stale" : "unknown";
      this.overviewChecks = [];
    },
    /** Повторная проверка: без reload, с тем же bounded pending. */
    refreshOverviewStatus() {
      if (this.overviewPending) return;
      this.loadOverviewStatus();
    },
    /** Upload fail-closed: открыт только при подтверждённом ready без проверки. */
    overviewUploadBlocked() {
      return this.overviewPending || this.overviewStatus !== "ready";
    },
    overviewProcessText() {
      const keys = {
        available: "overview.status.process.available",
        unknown: "overview.status.process.unknown",
      };
      return this.t(keys[this.overviewProcessHealth] || "overview.status.process.unavailable");
    },
    overviewReadinessText() {
      const keys = {
        ready: "overview.status.dependencies.ready",
        not_ready: "overview.status.dependencies.notReady",
        stale: "overview.status.dependencies.stale",
      };
      return this.t(keys[this.overviewStatus] || "overview.status.dependencies.unknown");
    },
    /** Только safe allowlisted reason: raw payload никогда не рендерится. */
    overviewRowReason(row) {
      if (!row || row.status === "ready") return "";
      return this.t(storageReasonKey(row.reason));
    },
    /** Только safe allowlisted action; reason allowlist не переиспользуется. */
    overviewRowAction(row) {
      if (!row || row.status === "ready") return "";
      return this.t(storageActionKey(row.action));
    },
    /** Connection test доступен ДО save; результат объявляется, но не сохраняет. */
    async testModelConnection() {
      if (this.modelTesting) return;
      this.modelTestRequired = false;
      this.modelSaved = false;
      this.modelSettingsError = "";
      const validation = validateModelDraft(this.modelDraftState());
      if (validation) {
        this.modelProbeId = "";
        this.modelTestStatus = "failed";
        this.modelTestReason = validation.reason;
        this.modelTestDimension = null;
        this.modelTestModel = "";
        return;
      }
      const token = this.modelDraftToken;
      const request = (Number(this.modelTestRequest) || 0) + 1;
      this.modelTestRequest = request;
      const epoch = captureSessionEpoch();
      const identity = captureSessionIdentity();
      this.modelTesting = true;
      this.modelProbeId = "";
      this.modelTestStatus = "testing";
      this.modelTestReason = "";
      this.modelTestDimension = null;
      try {
        const outcome = await testModelSettingsRequest(
          modelTestPayload(this.modelDraftState(), this.modelRevision),
        );
        if (
          token !== this.modelDraftToken ||
          !isSessionIdentityCurrent(identity) ||
          !isSessionEpochCurrent(epoch)
        ) {
          // Поздний ответ прежнего draft/сессии не публикуется.
          return;
        }
        if (!outcome.ok || !modelProbeAccepted(outcome.payload)) {
          this.modelTestStatus = "failed";
          this.modelTestReason = modelRefusalReason(outcome);
          this.modelTestDimension = null;
          this.modelTestModel = "";
          return;
        }
        this.modelProbeId = outcome.payload.probe_id;
        this.modelTestStatus = "ok";
        this.modelTestReason = "";
        this.modelTestModel = String(outcome.payload.declared_model || this.modelName);
        this.modelTestDimension = modelActualDimension(outcome.payload);
      } catch (_error) {
        if (token === this.modelDraftToken && isSessionIdentityCurrent(identity)) {
          this.modelTestStatus = "failed";
          this.modelTestReason = "connection_failed";
        }
      } finally {
        // Pending-флаг принадлежит конкретному запросу: edit-during-test его
        // освобождает через invalidateModelProbe, и поздний finally не должен
        // сбрасывать флаг уже запущенного нового теста.
        if (request === this.modelTestRequest) this.modelTesting = false;
      }
    },
    /**
     * Сохраняет только успешно проверенный draft. До PUT success никакого
     * подтверждения нет; конфликт revision/draft требует нового теста.
     */
    async submitModelSettings() {
      if (this.modelSaving) return;
      this.modelSaved = false;
      this.modelSettingsError = "";
      if (!this.modelProbeId || this.modelTestStatus !== "ok") {
        this.modelTestRequired = true;
        return;
      }
      const token = this.modelDraftToken;
      const epoch = captureSessionEpoch();
      const identity = captureSessionIdentity();
      const probeId = this.modelProbeId;
      this.modelSaving = true;
      try {
        const outcome = await saveModelSettingsRequest(
          modelSavePayload(this.modelDraftState(), this.modelRevision, probeId),
        );
        if (!isSessionIdentityCurrent(identity) || !isSessionEpochCurrent(epoch)) {
          return;
        }
        if (token !== this.modelDraftToken) {
          // PUT относится к прежнему draft. Если сервер его принял, новая
          // revision принадлежит accepted config: обновляем только её и
          // presence, не затирая текущий ввод. Probe израсходован.
          if (outcome.ok && outcome.payload && typeof outcome.payload === "object") {
            this.modelRevision = Number.isInteger(outcome.payload.revision)
              ? outcome.payload.revision
              : this.modelRevision;
            this.modelConfigured = true;
            this.modelCredentialPresent = outcome.payload.credential_present === true;
          }
          this.modelProbeId = "";
          this.modelTestStatus = "idle";
          this.modelTestRequired = outcome.ok;
          return;
        }
        if (!outcome.ok) {
          const reason = modelRefusalReason(outcome);
          if (
            outcome.status === 409 ||
            reason === "revision_mismatch" ||
            reason === "probe_revision_mismatch"
          ) {
            // Revision сменилась: показываем актуальное принятое состояние.
            await this.loadModelSettingsPanel();
            if (!isSessionIdentityCurrent(identity)) return;
          }
          this.invalidateModelProbe();
          this.modelTestRequired = true;
          this.modelSettingsError = modelReasonKey(reason);
          return;
        }
        this.applyModelSettings(outcome.payload);
        this.modelSaved = true;
        // Активный RAG-профиль/индексация намеренно не меняются.
        this.loadStorageDiagnostics();
      } catch (_error) {
        if (isSessionIdentityCurrent(identity) && isSessionEpochCurrent(epoch)) {
          this.modelSettingsError = "error.model.unavailable";
        }
      } finally {
        if (isSessionIdentityCurrent(identity)) this.modelSaving = false;
      }
    },
    modelTestResultText() {
      if (this.modelTestStatus === "ok") {
        return this.t("system.model.resultOk", {
          model: this.modelTestModel || this.modelName,
          dimension: String(this.modelTestDimension ?? ""),
        });
      }
      if (this.modelTestStatus === "testing") {
        return this.t("system.model.testing");
      }
      if (this.modelTestStatus === "failed") {
        return this.t("system.model.resultFailed", {
          reason: this.t(modelReasonKey(this.modelTestReason)),
        });
      }
      return this.t("system.model.idle");
    },
    storageDiagnosticsText() {
      if (this.storageDiagnosticsError) return this.t(this.storageDiagnosticsError);
      if (this.storageDiagnosticsStatus === "ready") return this.t("system.storage.ready");
      if (this.storageDiagnosticsStatus === "not_ready") return this.t("system.storage.notReady");
      return this.t("system.storage.unknown");
    },
    /** Story 1.11: статичный safe label выбранного graph backend. */
    storageRowLabel(row) {
      return this.t(storageComponentKey(row && row.component));
    },
    storageRowStatus(row) {
      return this.t(storageStatusKey(row && row.status));
    },
    /** Safe allowlisted reason; raw payload reason никогда не рендерится. */
    storageRowReason(row) {
      if (!row || !row.reason) return "";
      return this.t(storageReasonKey(row.reason));
    },
    modelCredentialPresenceText() {
      return this.t(
        this.modelCredentialPresent
          ? "system.model.credentialPresent"
          : "system.model.credentialAbsent",
      );
    },
    endSession() {
      this.authFlowVersion += 1;
      // C1: истечение/revoke завершает lifetime ТОЙ ЖЕ сессии. Lifetime-fence
      // закрывает поздние ответы прежнего lifetime (polling/данные не воскресают),
      // но не делает уже стартовавшую security-команду этой же сессии чужой —
      // подтверждённый сервером результат остаётся её собственным.
      fenceSessionLifetime();
      this.clearSessionScopedState();
      this.sessionNotice = "notice.sessionExpired";
      window.history.replaceState({}, "", "/login");
    },
    clearSessionScopedState() {
      if (this.documentsRefreshTimer) {
        clearTimeout(this.documentsRefreshTimer);
        this.documentsRefreshTimer = null;
      }
      this.documentsRefreshAttempts = 0;
      this.documentsRefreshSignature = "";
      this.stopActivityHeartbeat();

      // Завершение сессии не должно оставлять данные, формы, выбранные объекты
      // или индикаторы прежней сессии, которые могли бы воскреснуть и перезаписать
      // состояние новой сессии.
      this.authenticated = false;
      this.authUsername = "";
      this.section = "documents";
      this.navOpen = false;
      // Story 1.8: route-состояние прежней сессии не переезжает в новую.
      this.routeNotice = "";
      this.routeError = "";
      this.pendingRoute = null;
      this.appliedRouteSignature = "";

      // documents
      this.documents = [];
      this.documentsLoading = false;
      this.documentsError = "";
      this.documentSearch = "";
      this.documentStatusFilter = DOCUMENT_STATUS_ALL;
      this.documentsPage = 1;
      this.closeDocumentChunks();
      this.closeNormalizedDocument();

      // wizard / upload form
      this.uploadOpen = false;
      this.uploadBusy = false;
      this.uploadError = "";
      this.uploadTitle = "";
      // File bytes не сохраняются между сессиями; не обещаем их сохранность.
      this.uploadFile = null;
      this.uploadWizardDocumentId = "";
      this.uploadWizardDocumentTitle = "";
      this.documentWizardMode = "create";
      this.documentWizardEditId = "";
      this.documentWizardInitialTitle = "";
      this.documentWizardStep = 1;
      // F1: завершение сессии снимает и привязку draft к прежнему entity.
      // F4: незавершённая операция мастера становится устаревшей и здесь.
      this.documentWizardDraftKey = "";
      this.documentWizardDraftToken = (Number(this.documentWizardDraftToken) || 0) + 1;
      this.reprocessBusyId = "";
      this.deleteBusyId = "";
      this.clearDocumentWizardState();
      // Story 1.10: model draft/probe/credential прежней сессии не переезжают
      // в новую сессию. Поля сбрасываются здесь напрямую: existing regression
      // harness исполняет это тело метода в reduced sandbox без новых методов.
      this.modelSettingsLoading = false;
      this.modelSettingsError = "";
      this.modelConfigured = false;
      this.modelRevision = 0;
      this.modelProvider = "local";
      this.modelBaseUrl = "";
      this.modelName = "";
      this.modelDeclaredDimension = "";
      this.modelCredential = "";
      this.modelCredentialPresent = false;
      this.modelTesting = false;
      this.modelSaving = false;
      this.modelTestStatus = "idle";
      this.modelTestRequired = false;
      this.modelTestDimension = null;
      this.modelTestReason = "";
      this.modelTestModel = "";
      this.modelProbeId = "";
      this.modelDraftToken = (Number(this.modelDraftToken) || 0) + 1;
      this.modelTestRequest = (Number(this.modelTestRequest) || 0) + 1;
      this.modelSaved = false;
      this.storageDiagnosticsStatus = "unknown";
      this.storageDiagnosticsRows = [];
      this.storageDiagnosticsError = "";
      // Story 1.14: подтверждённое readiness прежней сессии не переезжает в
      // новую; незавершённая проверка становится устаревшей (request token).
      this.overviewStatus = "unknown";
      this.overviewProcessHealth = "unavailable";
      this.overviewChecks = [];
      this.overviewPending = false;
      this.overviewHasContract = false;
      this.overviewRequest = (Number(this.overviewRequest) || 0) + 1;

      // Story 1.6 / AC-3 (ui-contract §4–§5): подтверждённый выход, end-all,
      // смена пароля и истечение сессии безусловно удаляют safe return target.
      // Единственный production-потребитель — реальный `clearReturnTarget()` из
      // `js/return-target.js`; reduced extraction-sandbox существующих тестов
      // вызывают это тело метода вне модуля, поэтому для них тот же ключ
      // удаляется напрямую (без чтения и без записи, второй state-owner не
      // появляется).
      //
      // R2: отказ storage не прерывает очистку остального session-scoped
      // состояния — ни на самом объекте, ни на отдельном методе. Локальная
      // граница нужна и потому, что очистка вызывается из перехода логина:
      // исключение здесь оставило бы в памяти уже введённые пароли.
      try {
        if (typeof clearReturnTarget === "function") {
          clearReturnTarget();
        } else {
          globalThis.sessionStorage?.removeItem?.("freehub.returnTo.v1");
        }
      } catch (_error) {
        // Недоступный storage не превращает очистку в успех и не прерывает её.
      }
      this.passwordChangeOpen = false;
      this.passwordChangeBusy = false;
      this.passwordChangeError = "";
      this.passwordChangeCurrent = "";
      this.passwordChangeNew = "";
      this.passwordChangeConfirmation = "";
      this.systemAllSessionsConfirm = false;
      this.systemBusy = false;
      this.systemError = "";

      // retrieval
      this.retrievalQuery = "";
      this.retrievalBusy = false;
      this.retrievalAnswerBusy = false;
      this.retrievalError = "";
      this.retrievalResults = [];
      this.retrievalAppliedProfile = "";
      this.retrievalEngineProfile = "";
      this.retrievalAnswerProfile = "";
      this.retrievalAnswer = "";
      this.retrievalAnswerCitations = [];
      this.retrievalRuns = [];
      this.retrievalRunsLoading = false;
      this.retrievalRunsError = "";

      // evaluation
      this.evaluationRuns = [];
      this.evaluationRunsLoading = false;
      this.evaluationRunsError = "";
      this.evaluationRunBusy = false;
      this.evaluationStatusFilter = "all";
      this.evaluationProfileFilter = "all";
      this.evaluationDocumentIds = [];
      this.evaluationExportBusyId = "";
      this.evaluationCompareBaseId = "";
      this.evaluationCompareCandidateId = "";
      this.closeEvaluationCases();
      this.evaluationDatasets = [];
      this.evaluationDatasetsLoading = false;
      this.evaluationDatasetsError = "";
      this.selectedEvaluationDatasetId = "";
      this.evaluationDatasetEditName = "";
      this.evaluationDatasetEditVersion = "";
      this.evaluationDatasetCases = [];
      this.evaluationDatasetCasesLoading = false;
      this.evaluationDatasetCasesError = "";
      this.evaluationDatasetCreateBusy = false;
      this.evaluationDatasetUpdateBusy = false;
      this.evaluationDatasetDeleteBusyId = "";
      this.evaluationDatasetCaseCreateBusy = false;
      this.evaluationDatasetCaseDeleteBusyId = "";
      this.evaluationDatasetName = "";
      this.evaluationDatasetVersion = "v1";
      this.resetEvaluationCaseForm();

      // agents
      this.agents = [];
      this.agentsLoading = false;
      this.agentsError = "";
      this.agentCreateOpen = false;
      this.agentCreateBusy = false;
      this.resetAgentForm();
      this.closeAgentDocuments();
      this.closeAgentTest();
      this.agentProfileBusyId = "";
      this.agentProfileError = "";

      // logs / import jobs
      this.logs = [];
      this.logsLoading = false;
      this.logsError = "";
      this.logEventType = "";
      this.importJobs = [];
      this.importJobsLoading = false;
      this.importJobsError = "";
    },
    startActivityHeartbeat() {
      if (this.activityScheduler) return;
      const isVisible = () => document.visibilityState === "visible";
      this.activityScheduler = createActivityScheduler({
        send: sendSessionActivity,
        isVisible,
      });
      this.activityEvents = [...TRUSTED_ACTIVITY_EVENTS];
      this.activityListener = (event) => {
        // Polling/visibilitychange/timer и синтетические события не активность.
        if (!shouldTrackActivityEvent(event, isVisible)) return;
        this.activityScheduler.record();
      };
      for (const name of this.activityEvents) {
        document.addEventListener(name, this.activityListener, { passive: true, capture: true });
      }
    },
    stopActivityHeartbeat() {
      if (this.activityScheduler) {
        this.activityScheduler.stop();
        this.activityScheduler = null;
      }
      if (this.activityListener) {
        for (const name of this.activityEvents) {
          document.removeEventListener(name, this.activityListener, { capture: true });
        }
      }
      this.activityListener = null;
      this.activityEvents = [];
    },
    startAuthenticatedUi() {
      this.startActivityHeartbeat();
      // Story 1.8: начальный URL (включая уже валидированный return target)
      // разрешается router-ом ровно один раз; полного reload/remount нет.
      const route = this.initialRoute();
      // F3: запись того же route (reload внутри сессии) сохраняет свои
      // безопасные enum/page; потреблённый return target — новая запись и
      // получает детерминированный default.
      const state = this.pendingRoute
        ? readHistoryState(null)
        : initialRouteState(route, this.currentLocation(), window.history.state);
      this.pendingRoute = null;
      this.applyRouteState(state);
      this.writeRoute(route, "replace", state);
      this.routeNotice = "";
      if (route.name === ROUTE_WIZARD) {
        this.enterDocumentWizardRoute();
        return;
      }
      this.loadSection(route.section || "documents");
    },
    async initWizardPage() {
      const epoch = captureSessionEpoch();
      this.section = "documents";
      this.uploadOpen = true;
      this.uploadError = "";
      const params = new URLSearchParams(window.location.search);
      const documentId = params.get("documentId") || "";
      this.documentWizardMode = params.get("mode") === "edit" && documentId ? "edit" : "create";
      this.documentWizardEditId = this.documentWizardMode === "edit" ? documentId : "";
      // F1: draft мастера принадлежит конкретному route/entity. При входе в
      // мастер для другого объекта остатки прежнего draft (заголовок, вопрос,
      // файл, id) не переиспользуются — «допустимым» остаётся только resume того
      // же объекта из session-scoped storage ниже.
      // F4: новая привязка draft получает новый token операции; ответ PATCH
      // прежнего draft не публикуется в этот редактор.
      const draftKey =
        this.documentWizardMode === "edit" ? `edit:${this.documentWizardEditId}` : "create";
      if (this.documentWizardDraftKey !== draftKey) {
        this.clearDocumentWizardDraft();
        this.documentWizardDraftKey = draftKey;
        this.documentWizardDraftToken = (Number(this.documentWizardDraftToken) || 0) + 1;
      }
      this.restoreDocumentWizardState();
      await this.fetchDocuments();
      // Session могла смениться во время awaited fetchDocuments: устаревший
      // документ не гидратируется и состояние прежней сессии не публикуется.
      if (!isSessionEpochCurrent(epoch)) return;
      // Story 1.8: поздний ответ прежнего маршрута (пользователь уже ушёл Back-ом
      // из мастера) не восстанавливает wizard и не публикует чужое состояние.
      if (!this.uploadOpen || !this.isDocumentWizardPage()) return;
      if (this.documentWizardMode === "edit") this.hydrateDocumentWizardEditor();
    },
    /**
     * F1: единственная точка очистки in-memory draft мастера (заголовок, вопрос,
     * файл, связанный объект, ошибки, шаг). `sessionStorage` очищается отдельно
     * (`clearDocumentWizardState`): закрытие overlay обязано снимать и память, а
     * resume того же объекта после reload — по-прежнему работать.
     */
    clearDocumentWizardDraft() {
      this.uploadTitle = "";
      this.uploadFile = null;
      this.uploadError = "";
      // F4: снятый draft — уже не операция: идущий ответ PATCH не должен
      // вернуть сюда поля и шаг (token стал другим), а «занятость» формы
      // принадлежит прежнему draft и не «залипает» в следующем.
      this.uploadBusy = false;
      this.uploadWizardDocumentId = "";
      this.uploadWizardDocumentTitle = "";
      this.documentWizardInitialTitle = "";
      this.documentWizardStep = 1;
      this.retrievalQuery = "";
      this.retrievalResults = [];
      this.retrievalError = "";
      this.documentWizardDraftKey = "";
      this.documentWizardDraftToken = (Number(this.documentWizardDraftToken) || 0) + 1;
      if (this.$refs && this.$refs.documentFile) {
        this.$refs.documentFile.value = "";
      }
    },
    setSection(section, options = {}) {
      // Story 1.8: явный переход раздела — push ровно одной canonical записи;
      // canonicalisation/routine того же раздела — replace.
      const mode = options.history === "replace" ? "replace" : "push";
      this.writeRoute(sectionRoute(section), mode, this.routineRouteState());
      this.routeNotice = "";
      if (this.uploadOpen) this.closeWizardOverlay();
      this.loadSection(section);
    },
    /** Прежний набор загрузок раздела: state-owner не меняется. */
    loadSection(section) {
      this.section = section;
      if (section === "documents") {
        // C3: переход в «Документы» (в т.ч. после прямого landing в System)
        // обязан загрузить реальный список, а не показать пустой. Обычный старт
        // на «Документах» идёт своим путём в `startAuthenticatedUi`, поэтому
        // двойного initial-fetch здесь не возникает.
        this.fetchDocuments();
        // Story 1.14: landing показывает состояние системы, а первое действие
        // fail-closed. Вход в раздел = обычная повторная проверка.
        if (typeof this.loadOverviewStatus === "function") this.loadOverviewStatus();
      }
      if (section === "evaluation") {
        this.fetchDocuments({ background: true });
        this.fetchRetrievalRuns();
        this.fetchEvaluationRuns();
        this.fetchEvaluationDatasets();
      }
      if (section === "agents") {
        this.fetchAgents();
        this.fetchDocuments({ background: true });
      }
      if (section === "logs") {
        this.fetchLogs();
        this.fetchImportJobs();
      }
      if (section === "system") {
        // Story 1.10: чтение принятых настроек и read-only readiness при
        // входе в раздел/reload; внешняя смена storage env видна без migration.
        // `typeof`-guard: existing reduced-sandbox harness исполняет loadSection
        // вне полного component и не содержит новых методов.
        if (typeof this.loadModelSettingsPanel === "function") this.loadModelSettingsPanel();
        if (typeof this.loadStorageDiagnostics === "function") this.loadStorageDiagnostics();
      }
    },
    // --- Story 1.8: единственный router поверх прежнего session/domain owner ----
    /**
     * Router интерпретирует initial URL, popstate и явные переходы; fetch и
     * mutation остаются у существующих owners, второй state-owner не создаётся.
     */
    currentLocation() {
      const location = typeof window !== "undefined" ? window.location : null;
      return {
        pathname: location && typeof location.pathname === "string" ? location.pathname : "",
        search: location && typeof location.search === "string" ? location.search : "",
      };
    },
    currentHref() {
      const { pathname, search } = this.currentLocation();
      return `${pathname}${search}`;
    },
    /** Routine-состояние для истории: только действительно безопасные enum/page. */
    routineRouteState() {
      return { filter: this.documentStatusFilter, page: this.documentsPage };
    },
    /**
     * Пишет ровно одну canonical запись: явный переход/открытие объекта — push
     * (без дубля того же адреса), canonicalisation и routine — replace.
     */
    writeRoute(route, mode = "push", state = {}) {
      const canonical = serializeRoute(route);
      const nextState = routeHistoryState(route, state);
      const history = window.history;
      if (mode === "push" && canonical !== this.currentHref()) {
        history.pushState(nextState, "", canonical);
      } else {
        history.replaceState(nextState, "", canonical);
      }
      this.appliedRouteSignature = routeSignature(route, state);
      return canonical;
    },
    /**
     * Безопасное состояние раздела применяется только из allowlist.
     *
     * F3: состояние детерминировано — отсутствие filter/page в записи означает
     * default текущего route, а не унаследованное значение прежней записи или
     * завершённой сессии.
     */
    applyRouteState(state = {}) {
      this.documentStatusFilter = isSafeFilter(state.filter) ? state.filter : DOCUMENT_STATUS_ALL;
      this.documentsPage = isSafePage(state.page) ? state.page : 1;
    },
    /**
     * Начальный route: потреблённый return target применяется ровно один раз,
     * иначе раздел берётся прежним owner-ом (`initialSectionFromLocation`), а
     * wizard/login остаются своими route-формами.
     */
    initialRoute() {
      if (this.pendingRoute) return this.pendingRoute;
      const route = parseRoute(this.currentLocation());
      // F3/route-init: `/login` (в т.ч. `/login/`) — auth surface, а не route
      // приложения. С активной сессией он заменяется canonical разделом, а не
      // остаётся в адресе; цель возврата из него не выводится.
      if (route.name === "login") {
        return sectionRoute(this.initialSectionFromLocation());
      }
      if (route.name === ROUTE_WIZARD) return route;
      return sectionRoute(this.initialSectionFromLocation());
    },
    installRouteListener() {
      if (this.routePopStateListener) return;
      if (typeof window.addEventListener !== "function") return;
      this.routePopStateListener = () => this.handleRoutePopState();
      window.addEventListener("popstate", this.routePopStateListener);
    },
    /**
     * Popstate восстанавливает route/state без новой записи истории и без
     * повторной mutation; сессия и её cleanup остаются у прежнего owner-а.
     */
    handleRoutePopState() {
      // Пока сессия не разрешена (authChecking), текущий route неизвестен:
      // адрес не переписывается, иначе popstate во время проверки сессии
      // потерял бы цель пользователя. Разрешение route делает login-init.
      if (this.authChecking) return;
      if (!this.authenticated) {
        // Auth surface не является route-целью: Back/Forward не возвращают
        // пользователя на приложение без активной сессии.
        if (this.currentLocation().pathname !== "/login") {
          window.history.replaceState({}, "", "/login");
        }
        return;
      }
      this.applyRoute(parseRoute(this.currentLocation()), readHistoryState(window.history.state));
    },
    /**
     * Одно применение route: повторная та же подпись не переигрывает загрузку и
     * mutation, а состояние прежних разделов сохраняется в текущем runtime.
     */
    applyRoute(route, state = {}) {
      this.routeNotice = "";
      if (route.name === ROUTE_WIZARD) {
        this.enterDocumentWizardRoute();
        return;
      }
      const section = route.name === ROUTE_SECTION ? route.section : "documents";
      const signature = routeSignature(route, state);
      const repeat = signature === this.appliedRouteSignature && this.section === section;
      this.appliedRouteSignature = signature;
      this.section = section;
      // Popstate на раздел закрывает overlay мастера: его состояние не
      // «залипает» поверх другого маршрута.
      if (this.uploadOpen) this.closeWizardOverlay();
      this.applyRouteState(state);
      if (repeat) return;
      this.loadSection(section);
    },
    /** Закрытие overlay мастера без навигации: чужой раздел не получает его состояние. */
    closeWizardOverlay() {
      this.uploadOpen = false;
      this.uploadError = "";
      this.routeError = "";
      this.documentWizardMode = WIZARD_MODE_CREATE;
      this.documentWizardEditId = "";
      // F1: закрытие мастера снимает draft и в памяти, иначе заголовок/вопрос
      // прежнего объекта уехали бы в редактор следующего (PATCH чужого объекта).
      this.clearDocumentWizardDraft();
    },
    /**
     * Story 1.14 / R3: единая точка входа в мастер (create-кнопка, прямой
     * /wizard/, reload, history/popstate). Create-вход fail-closed: пока
     * готовность не подтверждена, мастер не открывается. Edit-вход не меняет
     * прежние story-сценарии (он не создаёт документ).
     */
    async enterDocumentWizardRoute() {
      const params = new URLSearchParams(window.location.search);
      const documentId = params.get("documentId") || "";
      const editEntry = params.get("mode") === "edit" && documentId;
      if (!editEntry && this.overviewUploadBlocked()) {
        // R3b: продолжение принадлежит конкретному wizard-входу и сессии. Уход на
        // другой раздел (push/popstate) или logout/relogin во время проверки
        // делают поздний ответ чужим: ни overlay, ни fallback-route не
        // применяются — session guard внутри loadOverviewStatus fence-ит только
        // саму карточку, не этот continuation.
        const epoch = captureSessionEpoch();
        const identity = captureSessionIdentity();
        const entryHref = this.currentHref();
        const entry = (Number(this.wizardEntryToken) || 0) + 1;
        this.wizardEntryToken = entry;
        // Одна bounded повторная проверка: прямой URL не должен требовать
        // ручного refresh, но и не открывает мастер по неизвестному состоянию.
        if (typeof this.loadOverviewStatus === "function") await this.loadOverviewStatus();
        const stillOurs =
          entry === this.wizardEntryToken &&
          isSessionIdentityCurrent(identity) &&
          isSessionEpochCurrent(epoch) &&
          this.currentHref() === entryHref;
        if (!stillOurs) return;
        if (this.overviewUploadBlocked()) {
          this.uploadOpen = false;
          this.section = "documents";
          this.routeNotice = "notice.readinessUnavailable";
          this.writeRoute(sectionRoute("documents"), "replace", {});
          this.fetchDocuments();
          return;
        }
      }
      this.initWizardPage();
    },
    /** AC-3/SYS-UX-01: после fallback на список заголовок получает focus. */
    focusShellHeading() {
      const heading = this.$refs ? this.$refs.shellHeading : null;
      if (!heading || typeof heading.focus !== "function") return;
      heading.focus();
      // Shell может быть ещё не отрисован в момент fallback (auth/x-show):
      // focus на элементе внутри display:none теряется в body. Один повтор после
      // отрисовки — только если пользователь сам не перевёл focus на другой
      // контрол (без цикла и без перехвата чужого focus).
      if (
        typeof document !== "undefined" &&
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function" &&
        document.activeElement !== heading
      ) {
        window.requestAnimationFrame(() => {
          if (!heading.isConnected) return;
          const active = document.activeElement;
          if (active && active !== document.body) return;
          heading.focus();
        });
      }
    },
    /** AC-2: skip link переводит focus в main — не только меняет hash. */
    focusMain() {
      const main = this.$refs ? this.$refs.shellMain : null;
      if (main && typeof main.focus === "function") main.focus();
    },
    // --- AC-2: единственный nav и один владелец открытого состояния ----------
    /**
     * Открытие/закрытие мобильного меню. Trigger стоит в DOM после nav, поэтому
     * обычный прямой Tab от кнопки уходил бы к аватару, минуя открытые пункты.
     * При клавиатурной активации (Enter/Space даёт click с detail === 0) focus
     * переводится на первый пункт nav — дальше Tab идёт по пунктам меню.
     * Мышиное открытие focus не перехватывает.
     */
    toggleNavMenu(event) {
      const opening = !this.navOpen;
      this.navOpen = opening;
      if (!opening || !event || event.detail !== 0) return;
      // Клавиатурный пользователь может нажать Tab сразу после Enter: Alpine
      // применяет класс на следующем тике, а focus() в display:none не работает,
      // поэтому состояние «открыто» применяется синхронно перед переносом focus
      // (значение navOpen уже true, биндинг Alpine оставляет тот же класс).
      const nav = this.$refs ? this.$refs.navMenu : null;
      if (nav && nav.classList) nav.classList.add("open");
      this.focusFirstNavItem();
    },
    /** Первый доступный пункт единственного nav после клавиатурного открытия. */
    focusFirstNavItem() {
      const nav = this.$refs ? this.$refs.navMenu : null;
      if (!nav || typeof nav.querySelector !== "function") return;
      const first = nav.querySelector("button, a");
      if (first && typeof first.focus === "function") first.focus();
    },
    /**
     * Закрытие мобильного меню. `restoreFocus` используется Escape-ом: focus
     * возвращается на trigger, чтобы клавиатурный пользователь не потерялся.
     * Когда меню уже закрыто, обработчик ничего не делает (не перехватывает
     * Escape у других overlay).
     */
    closeNavMenu(options = {}) {
      if (!this.navOpen) return;
      this.navOpen = false;
      if (options && options.restoreFocus === true) {
        const trigger = this.$refs ? this.$refs.navTrigger : null;
        if (trigger && typeof trigger.focus === "function") trigger.focus();
      }
    },
    /** Выбор пункта меню: существующий setSection + focus заголовка на mobile. */
    selectNavSection(section) {
      const wasOpen = this.navOpen;
      this.navOpen = false;
      this.setSection(section);
      if (wasOpen) this.focusShellHeading();
    },
    /** AC-4: копируется ровно показанное public community name. */
    workspaceName() {
      const name = this.$refs ? this.$refs.wsCurrentName : null;
      return (name && name.textContent ? name.textContent : "").trim();
    },
    async copyWorkspaceName() {
      // Сброс делает повтор завершённой операции снова объявляемым (ровно одна
      // запись на завершённую операцию), при этом исходный провал не выдаётся
      // за успех.
      this.copyNotice = "";
      try {
        const clipboard = typeof navigator !== "undefined" ? navigator.clipboard : null;
        if (!clipboard || typeof clipboard.writeText !== "function") {
          throw new Error("clipboard unavailable");
        }
        await clipboard.writeText(this.workspaceName());
        this.copyNotice = "shell.ws.copySuccess";
      } catch (_error) {
        this.copyNotice = "shell.ws.copyError";
      }
    },
    initialSectionFromLocation() {
      const section = new URLSearchParams(window.location.search).get("section");
      return ["documents", "agents", "evaluation", "logs", "system"].includes(section)
        ? section
        : "documents";
    },
    openSectionFromWizard(section) {
      this.clearDocumentWizardState();
      // Story 1.8: переход из мастера — обычная внутренняя навигация (push +
      // закрытие overlay), без полного reload/remount.
      this.setSection(section);
    },
    title() {
      return this.t(`shell.title.${this.section}`);
    },
    subtitle() {
      return this.t(`shell.subtitle.${this.section}`);
    },
    /// Safe return target: canonical route текущего адреса, если пользователь
    /// явно целился в раздел/объект. `/` (Documents) и `/login` целью возврата не
    /// являются, а свободные query-параметры в target не переезжают: router
    /// отдаёт только разрешённые route-поля (section/mode/documentId), поэтому
    /// валидная edit-ссылка сохраняется целиком, а произвольный текст — нет.
    /// Проверку по-прежнему выполняет единственный владелец `return-target.js`.
    pendingReturnTargetFromLocation() {
      const route = parseRoute(this.currentLocation());
      if (route.name === "login") return null;
      const canonical = serializeRoute(route);
      // Documents (`/`) — не цель возврата: пользователь не «целился» в неё.
      if (canonical === serializeRoute(sectionRoute("documents"))) return null;
      return canonical;
    },
    async fetchDocuments(options = {}) {
      const epoch = captureSessionEpoch();
      const background = Boolean(options.background);
      if (!background) {
        this.documentsLoading = true;
      }
      this.documentsError = "";

      try {
        const response = await apiFetch("/api/documents", {
          headers: { Accept: "application/json" },
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          const message = await response.text();
          if (message.includes('"api_unavailable"')) {
            throw new Error("Сервер FreeHub временно недоступен");
          }
          throw new Error(message || "Не удалось поставить документ в обработку");
        }
        const documents = await response.json();
        // Поздний успех прежней сессии не воскрешает данные и не возобновляет опрос.
        if (!isSessionEpochCurrent(epoch)) return;
        this.documents = documents;
        this.syncSelectedDocument();
        this.syncEvaluationDocuments();
        this.syncDocumentsPage();
        this.scheduleDocumentsRefresh();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.documentsError = error.message || "Не удалось загрузить документы";
      } finally {
        if (!background && isSessionEpochCurrent(epoch)) {
          this.documentsLoading = false;
        }
      }
    },
    scheduleDocumentsRefresh() {
      if (this.documentsRefreshTimer) {
        clearTimeout(this.documentsRefreshTimer);
        this.documentsRefreshTimer = null;
      }
      if (!this.hasPendingDocuments()) {
        this.documentsRefreshAttempts = 0;
        this.documentsRefreshSignature = "";
        return;
      }
      const nextSignature = this.pendingDocumentsSignature();
      if (nextSignature !== this.documentsRefreshSignature) {
        this.documentsRefreshSignature = nextSignature;
        this.documentsRefreshAttempts = 0;
      }
      const delay = Math.min(
        DOCUMENTS_REFRESH_MIN_DELAY_MS * 2 ** this.documentsRefreshAttempts,
        DOCUMENTS_REFRESH_MAX_DELAY_MS,
      );
      this.documentsRefreshAttempts += 1;
      this.documentsRefreshTimer = setTimeout(() => {
        this.fetchDocuments({ background: true });
      }, delay);
    },
    hasPendingDocuments() {
      return this.documents.some((document) =>
        ["registered", "processing"].includes(document.status || "registered"),
      );
    },
    pendingDocumentsSignature() {
      return this.documents
        .filter((document) => ["registered", "processing"].includes(document.status || "registered"))
        .map((document) =>
          [
            document.id,
            document.status || "registered",
            document.current_version_id || "",
            document.chunk_count || 0,
            document.updated_at || "",
          ].join(":"),
        )
        .sort()
        .join("|");
    },
    filteredDocuments() {
      return filterDocuments(this.documents, {
        query: this.documentSearch,
        status: this.documentStatusFilter,
      });
    },
    pagedDocuments() {
      const start = (this.documentsPage - 1) * this.documentsPageSize;
      return this.filteredDocuments().slice(start, start + this.documentsPageSize);
    },
    documentsTotalPages() {
      return Math.max(1, Math.ceil(this.filteredDocuments().length / this.documentsPageSize));
    },
    documentsPageStart() {
      const total = this.filteredDocuments().length;
      return total === 0 ? 0 : (this.documentsPage - 1) * this.documentsPageSize + 1;
    },
    documentsPageEnd() {
      return Math.min(this.documentsPage * this.documentsPageSize, this.filteredDocuments().length);
    },
    documentsPageNumbers() {
      return Array.from({ length: this.documentsTotalPages() }, (_item, index) => index + 1);
    },
    goDocumentsPage(page) {
      const nextPage = Math.min(this.documentsTotalPages(), Math.max(1, Number(page) || 1));
      this.documentsPage = nextPage;
      // Story 1.8: routine-обновление страницы — replace, без новой записи истории.
      this.syncRoutineRoute();
    },
    /** Routine status/page update того же раздела: canonical replace. */
    syncRoutineRoute() {
      if (this.section !== "documents") return;
      this.writeRoute(sectionRoute("documents"), "replace", this.routineRouteState());
    },
    syncDocumentsPage() {
      if (this.documentsPage > this.documentsTotalPages()) {
        this.documentsPage = this.documentsTotalPages();
      }
    },
    documentTabCount(status) {
      if (status === DOCUMENT_STATUS_ALL) {
        return this.documents.length;
      }
      return this.documents.filter((document) => documentStatus(document) === status).length;
    },
    setDocumentStatusFilter(status) {
      this.documentStatusFilter = status;
      this.documentsPage = 1;
      // Story 1.8: routine tab/filter — replace, Back не создаёт вторую запись.
      this.syncRoutineRoute();
    },
    documentFilterSummary() {
      const visible = this.filteredDocuments().length;
      const total = this.documents.length;
      if (visible === total) {
        return `${total} документов`;
      }
      return `${visible} из ${total}`;
    },
    documentsReadyCount() {
      return this.documents.filter((document) => document.status === "completed").length;
    },
    documentsPendingCount() {
      return this.documents.filter((document) =>
        ["registered", "processing"].includes(document.status || "registered"),
      ).length;
    },
    documentsFailedCount() {
      return this.documents.filter((document) => document.status === "failed").length;
    },
    documentsChunkCount() {
      return this.documents.reduce((total, document) => total + Number(document.chunk_count || 0), 0);
    },
    syncSelectedDocument() {
      if (this.uploadWizardDocumentId) {
        const uploadDocument = this.documents.find(
          (document) => document.id === this.uploadWizardDocumentId,
        );
        if (uploadDocument) {
          this.uploadWizardDocumentTitle = uploadDocument.title;
          this.persistDocumentWizardState();
        }
      }
      if (this.selectedDocumentId) {
        const selected = this.documents.find((document) => document.id === this.selectedDocumentId);
        if (!selected) {
          this.closeDocumentChunks();
        } else {
          this.selectedDocumentTitle = selected.title;
        }
      }
      if (this.selectedNormalizedDocumentId) {
        const normalizedSelected = this.documents.find(
          (document) => document.id === this.selectedNormalizedDocumentId,
        );
        if (!normalizedSelected) {
          this.closeNormalizedDocument();
        } else {
          this.selectedNormalizedDocumentTitle = normalizedSelected.title;
        }
      }
    },
    syncEvaluationDocuments() {
      const availableIds = new Set(this.evaluationDocuments().map((document) => document.id));
      this.evaluationDocumentIds = this.evaluationDocumentIds.filter((documentId) =>
        availableIds.has(documentId),
      );
    },
    openDocumentEditor(document) {
      if (!document?.id) return;
      this.clearDocumentWizardState();
      // Story 1.8: открытие объекта в мастере — push canonical edit-route и
      // применение его тем же router-ом (без полного reload/remount).
      this.openWizardRoute(wizardRoute(WIZARD_MODE_EDIT, document.id));
    },
    /**
     * Открытие мастера как route-entry: одна canonical запись (create — push)
     * и применение через тот же router.
     */
    openDocumentWizardPage() {
      // Story 1.14: первое действие fail-closed. disabled/aria-disabled —
      // видимая граница, эта проверка — фактическая (клик не открывает мастер
      // при unknown/stale/not_ready/pending).
      if (this.overviewUploadBlocked()) return;
      this.openWizardRoute(wizardRoute(WIZARD_MODE_CREATE, ""));
    },
    /** Применение wizard-route: URL один раз, затем общий fail-closed entry. */
    openWizardRoute(route) {
      this.routeNotice = "";
      this.writeRoute(route, "push", {});
      this.enterDocumentWizardRoute();
    },
    downloadOriginalDocument(document) {
      if (!document?.id) {
        return;
      }
      const anchor = window.document.createElement("a");
      anchor.href = `/api/documents/${document.id}/source`;
      anchor.rel = "noopener";
      anchor.click();
    },
    async saveWizardDocumentChanges() {
      const epoch = captureSessionEpoch();
      // F4: публикация ответа принадлежит конкретному draft/операции мастера.
      // Смена route/entity во время await (Back на список, другой документ,
      // закрытие мастера, новая сессия) делает операцию устаревшей: её ответ
      // не пишет поля, шаг, storage и «занятость» текущего редактора, поэтому
      // вызывающий не продолжает шаг (возврат false).
      const draftToken = Number(this.documentWizardDraftToken) || 0;
      const draftIsCurrent = () => (Number(this.documentWizardDraftToken) || 0) === draftToken;
      const document = this.uploadWizardDocument();
      const title = this.uploadTitle.trim();
      if (!document || !title) {
        this.uploadError = "Название документа обязательно";
        return false;
      }
      if (title === this.documentWizardInitialTitle) return true;

      this.uploadBusy = true;
      this.uploadError = "";

      try {
        const response = await apiFetch(`/api/documents/${encodeURIComponent(document.id)}`, {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ title }),
        });
        if (!isSessionEpochCurrent(epoch)) return false;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const updated = await response.json();
        if (!isSessionEpochCurrent(epoch)) return false;
        // Успешный PATCH своего объекта — факт и для общего списка (в т.ч. когда
        // пользователь уже на другом route). Поля же draft публикует только
        // текущая операция: иначе ответ A переписал бы редактор B.
        this.documents = this.documents.map((item) =>
          item.id === updated.id ? updated : item,
        );
        if (!draftIsCurrent()) return false;
        this.uploadWizardDocumentTitle = updated.title;
        this.documentWizardInitialTitle = updated.title;
        this.persistDocumentWizardState();
        return true;
      } catch (error) {
        if (!isSessionEpochCurrent(epoch) || !draftIsCurrent()) return false;
        this.uploadError = error.message || "Не удалось сохранить документ";
        return false;
      } finally {
        if (isSessionEpochCurrent(epoch) && draftIsCurrent()) {
          this.uploadBusy = false;
        }
      }
    },
    async openDocumentChunks(document) {
      const epoch = captureSessionEpoch();
      this.closeNormalizedDocument();
      this.selectedDocumentId = document.id;
      this.selectedDocumentTitle = document.title;
      this.documentChunks = [];
      this.documentChunksLoading = true;
      this.documentChunksError = "";

      try {
        const response = await apiFetch(`/api/documents/${encodeURIComponent(document.id)}/chunks`, {
          headers: { Accept: "application/json" },
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.documentChunks = payload.chunks || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.documentChunksError = error.message || "Не удалось загрузить фрагменты";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.documentChunksLoading = false;
      }
    },
    closeDocumentChunks() {
      this.selectedDocumentId = "";
      this.selectedDocumentTitle = "";
      this.documentChunks = [];
      this.documentChunksError = "";
      this.documentChunksLoading = false;
    },
    async openNormalizedDocument(document) {
      const epoch = captureSessionEpoch();
      this.closeDocumentChunks();
      this.selectedNormalizedDocumentId = document.id;
      this.selectedNormalizedDocumentTitle = document.title;
      this.normalizedDocument = null;
      this.normalizedObjectKey = "";
      this.normalizedDocumentLoading = true;
      this.normalizedDocumentError = "";

      try {
        const response = await apiFetch(`/api/documents/${encodeURIComponent(document.id)}/normalized`, {
          headers: { Accept: "application/json" },
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.normalizedDocument = payload.document;
        this.normalizedObjectKey = payload.normalized_object_key || "";
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.normalizedDocumentError =
          error.message || "Не удалось загрузить нормализованный документ";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.normalizedDocumentLoading = false;
      }
    },
    closeNormalizedDocument() {
      this.selectedNormalizedDocumentId = "";
      this.selectedNormalizedDocumentTitle = "";
      this.normalizedDocument = null;
      this.normalizedObjectKey = "";
      this.normalizedDocumentError = "";
      this.normalizedDocumentLoading = false;
    },
    selectUploadFile(event) {
      const [file] = event.target.files;
      this.uploadFile = file || null;
      if (this.uploadFile && !this.uploadTitle) {
        this.uploadTitle = this.uploadFile.name.replace(/\.[^.]+$/, "");
      }
      this.persistDocumentWizardState();
    },
    selectUploadDrop(event) {
      const [file] = event.dataTransfer.files;
      this.uploadFile = file || null;
      if (this.uploadFile && !this.uploadTitle) {
        this.uploadTitle = this.uploadFile.name.replace(/\.[^.]+$/, "");
      }
      this.persistDocumentWizardState();
      if (this.$refs.documentFile) {
        this.$refs.documentFile.value = "";
      }
    },
    openDocumentWizard(reset = false) {
      if (reset) {
        this.resetUploadWizard();
      }
      this.documentWizardMode = "create";
      this.documentWizardEditId = "";
      this.documentWizardInitialTitle = "";
      this.uploadOpen = true;
      this.setDocumentWizardStep(this.uploadWizardDocumentId ? 3 : 1);
      this.uploadError = "";
    },
    isDocumentWizardEditMode() {
      return this.documentWizardMode === "edit";
    },
    hydrateDocumentWizardEditor() {
      const document = this.documents.find((item) => item.id === this.documentWizardEditId);
      if (!document) {
        // AC-3: успешный (в т.ч. пустой) список — «документа больше нет»;
        // network/503 — честная недоступность, а не «документ удалён».
        if (this.documentsError) {
          this.routeError = "error.route.documentsUnavailable";
          return;
        }
        this.handleMissingDocument();
        return;
      }
      // F1: редактор гидратируется только своим объектом. Если draft принадлежит
      // другому entity (или никому), он снимается: заголовок/вопрос одного
      // документа не могут стать PATCH-ом другого.
      const draftKey = `edit:${document.id}`;
      // F4: draft привязывается к entity вместе с token-ом операции — ответ
      // PATCH другого (или уже закрытого) редактора сюда не публикуется.
      if (this.documentWizardDraftKey !== draftKey) {
        this.clearDocumentWizardDraft();
        this.documentWizardDraftKey = draftKey;
        this.documentWizardDraftToken = (Number(this.documentWizardDraftToken) || 0) + 1;
      }
      this.uploadWizardDocumentId = document.id;
      this.uploadWizardDocumentTitle = document.title;
      this.uploadTitle = this.uploadTitle || document.title;
      this.documentWizardInitialTitle = document.title;
      this.retrievalQuery = this.retrievalQuery || document.title;
      this.persistDocumentWizardState();
    },
    /**
     * AC-3: отсутствующий entity не оставляет пользователя в мастере на
     * несуществующем объекте: тот же router возвращает к списку (replace, без
     * новой записи истории), переход объясняется локализованным notice, а
     * заголовок раздела получает focus.
     */
    handleMissingDocument() {
      this.closeWizardOverlay();
      this.clearDocumentWizardState();
      this.section = "documents";
      this.routeNotice = "notice.documentMissing";
      this.writeRoute(sectionRoute("documents"), "replace", {});
      this.$nextTick(() => this.focusShellHeading());
    },
    closeDocumentWizard() {
      this.clearDocumentWizardState();
      this.closeWizardOverlay();
      if (this.isDocumentWizardPage()) {
        // Story 1.8: возврат к списку — внутренняя навигация (push + прежний
        // owner загрузки списка), без полного reload/remount.
        this.writeRoute(sectionRoute("documents"), "push", {});
        this.loadSection("documents");
      }
    },
    isDocumentWizardPage() {
      return window.location.pathname.replace(/\/+$/, "") === "/wizard";
    },
    restoreDocumentWizardState() {
      let restored = null;
      try {
        restored = JSON.parse(sessionStorage.getItem(DOCUMENT_WIZARD_STORAGE_KEY) || "null");
      } catch (_error) {
        restored = null;
      }
      if (!restored || typeof restored !== "object") {
        this.documentWizardStep = 1;
        return;
      }

      if (this.isDocumentWizardEditMode()) {
        if (
          restored.documentWizardMode !== "edit" ||
          restored.documentWizardEditId !== this.documentWizardEditId
        ) {
          this.documentWizardStep = 1;
          return;
        }
        this.documentWizardStep = Math.min(5, Math.max(1, Number(restored.documentWizardStep) || 1));
        this.uploadTitle = String(restored.uploadTitle || "");
        this.retrievalQuery = String(restored.retrievalQuery || "");
        return;
      }

      if (restored.documentWizardMode === "edit") {
        this.documentWizardStep = 1;
        return;
      }

      const documentId = String(restored.uploadWizardDocumentId || "");
      const step = Number(restored.documentWizardStep || 1);
      this.uploadTitle = String(restored.uploadTitle || "");
      this.uploadWizardDocumentId = documentId;
      this.uploadWizardDocumentTitle = String(restored.uploadWizardDocumentTitle || "");
      this.retrievalQuery = String(restored.retrievalQuery || "");

      if (documentId) {
        this.documentWizardStep = Math.min(5, Math.max(3, Number.isFinite(step) ? step : 3));
        return;
      }

      this.documentWizardStep = 1;
    },
    persistDocumentWizardState() {
      if (!this.uploadOpen && !this.isDocumentWizardPage()) {
        return;
      }

      const payload = {
        documentWizardMode: this.documentWizardMode,
        documentWizardEditId: this.documentWizardEditId,
        documentWizardStep: this.documentWizardStep,
        uploadTitle: this.uploadTitle,
        uploadWizardDocumentId: this.uploadWizardDocumentId,
        uploadWizardDocumentTitle: this.uploadWizardDocumentTitle,
        retrievalQuery: this.retrievalQuery,
      };

      try {
        sessionStorage.setItem(DOCUMENT_WIZARD_STORAGE_KEY, JSON.stringify(payload));
      } catch (_error) {
        // Сессия wizard не критична: если хранилище недоступно, форма не восстановится после перезагрузки.
      }
    },
    clearDocumentWizardState() {
      try {
        sessionStorage.removeItem(DOCUMENT_WIZARD_STORAGE_KEY);
      } catch (_error) {
        // Игнорируем недоступное хранилище.
      }
    },
    setDocumentWizardStep(step) {
      this.documentWizardStep = Math.min(5, Math.max(1, Number(step) || 1));
      this.persistDocumentWizardState();
    },
    documentWizardStepsMeta() {
      return [
        { number: 1, name: "Файл" },
        { number: 2, name: "Карточка" },
        { number: 3, name: "Обработка" },
        { number: 4, name: "Индексация" },
        { number: 5, name: "Проверка" },
      ];
    },
    documentWizardProgress() {
      return Math.round((this.documentWizardStep / 5) * 100);
    },
    documentWizardStepClass(step) {
      if (step === this.documentWizardStep) return "active";
      if (step < this.documentWizardStep) return "done";
      return "pending";
    },
    documentWizardStepStatusLabel(step) {
      if (step === this.documentWizardStep) return "В работе";
      if (step < this.documentWizardStep) return "Готово";
      return "Не начато";
    },
    documentWizardStepIndicator(step) {
      return step < this.documentWizardStep ? "✓" : String(step);
    },
    async goToDocumentWizardStep(step) {
      if (
        this.isDocumentWizardEditMode() &&
        this.documentWizardStep === 2 &&
        step !== 2 &&
        !(await this.saveWizardDocumentChanges())
      ) {
        return;
      }
      this.setDocumentWizardStep(step);
    },
    previousDocumentWizardStep() {
      if (this.documentWizardStep > 1) {
        this.setDocumentWizardStep(this.documentWizardStep - 1);
      }
    },
    canContinueDocumentWizard() {
      if (this.isDocumentWizardEditMode()) {
        if (this.documentWizardStep === 2) return Boolean(this.uploadTitle.trim());
        return Boolean(this.uploadWizardDocumentId);
      }
      if (this.documentWizardStep === 1) return Boolean(this.uploadFile);
      if (this.documentWizardStep === 2) return Boolean(this.uploadFile && this.uploadTitle.trim());
      if (this.documentWizardStep === 3) {
        return Boolean(this.uploadWizardDocumentId || (this.uploadFile && !this.uploadBusy));
      }
      if (this.documentWizardStep === 4) return Boolean(this.uploadWizardDocumentId);
      return true;
    },
    async nextDocumentWizardStep() {
      this.uploadError = "";

      if (this.documentWizardStep === 5) {
        if (this.isDocumentWizardEditMode() && !(await this.saveWizardDocumentChanges())) return;
        this.closeDocumentWizard();
        return;
      }

      if (
        this.isDocumentWizardEditMode() &&
        this.documentWizardStep === 2 &&
        !(await this.saveWizardDocumentChanges())
      ) {
        return;
      }

      if (this.documentWizardStep === 3 && !this.uploadWizardDocumentId) {
        // F4: продолжение шага принадлежит тому же draft, что и загрузка;
        // смена route/entity во время awaited POST не продвигает чужой мастер.
        const draftToken = Number(this.documentWizardDraftToken) || 0;
        await this.uploadDocument();
        if ((Number(this.documentWizardDraftToken) || 0) !== draftToken) return;
        if (!this.uploadWizardDocumentId) {
          return;
        }
      }

      if (this.documentWizardStep === 4 && this.uploadWizardDocument()) {
        this.retrievalQuery = this.uploadWizardDocument().title;
        this.persistDocumentWizardState();
      }

      if (this.canContinueDocumentWizard()) {
        this.setDocumentWizardStep(this.documentWizardStep + 1);
      }
    },
    resetUploadWizard() {
      if (this.isDocumentWizardEditMode()) {
        const document = this.uploadWizardDocument();
        this.uploadTitle = document?.title || this.documentWizardInitialTitle;
        this.retrievalQuery = document?.title || "";
        this.uploadError = "";
        this.persistDocumentWizardState();
        return;
      }
      // F1: «Очистить» — тот же in-memory reset, что и закрытие overlay.
      this.clearDocumentWizardDraft();
      this.clearDocumentWizardState();
    },
    uploadWizardDocument() {
      return (
        this.documents.find((document) => document.id === this.uploadWizardDocumentId) || null
      );
    },
    uploadWizardSteps() {
      const document = this.uploadWizardDocument();
      return document ? documentProcessingSteps(document) : [];
    },
    uploadWizardProcessingProgress() {
      return this.processingStepsProgress(this.uploadWizardSteps());
    },
    processingStepsProgress(steps) {
      if (!steps.length) {
        return 0;
      }
      const done = steps.reduce((total, step) => {
        if (["completed", "ready"].includes(step.state)) return total + 1;
        if (step.state === "active") return total + 0.5;
        return total;
      }, 0);
      return Math.min(100, Math.round((done / steps.length) * 100));
    },
    uploadWizardSummary() {
      const document = this.uploadWizardDocument();
      return document ? documentProcessingSummary(document) : null;
    },
    documentWizardDocumentReady() {
      return this.documentCanRunRetrieval(this.uploadWizardDocument());
    },
    documentCanRunRetrieval(document) {
      return document ? canRunDocumentRetrieval(document) : false;
    },
    async documentUploadErrorMessage(response) {
      const text = await response.text();
      if (
        response.status === 413 ||
        text.includes("Request Entity Too Large") ||
        text.includes("file is larger than")
      ) {
        return "Файл слишком большой. Максимальный размер документа: 25 МБ.";
      }
      if (text.trim().startsWith("<")) {
        return `Сервер отклонил загрузку документа: HTTP ${response.status}`;
      }
      // Story 1.14 / R3: JSON-отказ (в т.ч. structured readiness 503) не
      // рендерится сырым: reason/action/message остаются safe-ключами.
      if (text.trim().startsWith("{")) {
        return response.status === 503
          ? this.t("error.upload.notReady")
          : `Сервер отклонил загрузку документа: HTTP ${response.status}`;
      }
      return text || "Не удалось загрузить документ";
    },
    async uploadDocument() {
      // Story 1.14: фактическая граница upload, а не только disabled/aria.
      // unknown/stale/not_ready/pending и текущая проверка закрывают POST.
      if (this.overviewUploadBlocked()) {
        this.uploadError = this.t("error.upload.notReady");
        return;
      }
      const epoch = captureSessionEpoch();
      // F4: создание документа — тоже операция конкретного draft. Пока POST в
      // пути, пользователь может уйти Back-ом/на другой объект; такой ответ не
      // публикует id/заголовок/файл и не подменяет текущий редактор.
      const draftToken = Number(this.documentWizardDraftToken) || 0;
      const draftIsCurrent = () => (Number(this.documentWizardDraftToken) || 0) === draftToken;
      if (!this.uploadFile) {
        this.uploadError = "Выберите файл";
        return;
      }

      this.uploadBusy = true;
      this.uploadError = "";

      const formData = new FormData();
      formData.append("title", this.uploadTitle);
      formData.append("file", this.uploadFile);

      try {
        const response = await apiFetch("/api/documents", {
          method: "POST",
          body: formData,
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await this.documentUploadErrorMessage(response));
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        if (!draftIsCurrent()) {
          // Документ действительно создан — список обновляется, но поля чужого
          // (текущего) редактора не переписываются.
          await this.fetchDocuments();
          return;
        }
        this.uploadWizardDocumentId = payload.document.id;
        this.uploadWizardDocumentTitle = payload.document.title;
        this.uploadTitle = "";
        this.uploadFile = null;
        this.$refs.documentFile.value = "";
        this.persistDocumentWizardState();
        await this.fetchDocuments();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch) || !draftIsCurrent()) return;
        this.uploadError = error.message || "Не удалось загрузить документ";
      } finally {
        if (isSessionEpochCurrent(epoch) && draftIsCurrent()) {
          this.uploadBusy = false;
        }
      }
    },
    async reprocessDocument(document) {
      const epoch = captureSessionEpoch();
      // QA-P5: «Повторить обработку» — операция конкретного draft/редактора.
      // Сообщение об отказе принадлежит своей операции, поэтому публикуется только
      // в тот же draft (token) и на той же поверхности, где операция началась:
      // поздний отказ прежнего редактора после Back/другого объекта/закрытия
      // мастера не попадает в чужое состояние. Текущий route на момент ответа
      // здесь не решает, куда писать сообщение.
      const draftToken = Number(this.documentWizardDraftToken) || 0;
      const draftIsCurrent = () => (Number(this.documentWizardDraftToken) || 0) === draftToken;
      const startedInWizard = this.isDocumentWizardPage();
      this.reprocessBusyId = document.id;
      this.documentsError = "";
      if (startedInWizard) this.uploadError = "";

      try {
        const response = await apiFetch(`/api/documents/${encodeURIComponent(document.id)}/reprocess`, {
          method: "POST",
          headers: { Accept: "application/json" },
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        await this.fetchDocuments();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        const message = error.message || "Не удалось поставить документ в обработку";
        if (startedInWizard) {
          // Свой текущий редактор показывает свой отказ; чужой/уже закрытый —
          // не получает сообщение прежней операции.
          if (!draftIsCurrent()) return;
          this.uploadError = message;
        } else {
          this.documentsError = message;
        }
      } finally {
        if (isSessionEpochCurrent(epoch)) this.reprocessBusyId = "";
      }
    },
    async deleteDocument(document) {
      const epoch = captureSessionEpoch();
      if (!window.confirm(`Удалить документ "${document.title}"?`)) {
        return;
      }

      this.deleteBusyId = document.id;
      this.documentsError = "";

      try {
        const response = await apiFetch(`/api/documents/${encodeURIComponent(document.id)}`, {
          method: "DELETE",
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        if (this.selectedDocumentId === document.id) {
          this.closeDocumentChunks();
        }
        if (this.selectedNormalizedDocumentId === document.id) {
          this.closeNormalizedDocument();
        }
        await this.fetchDocuments();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.documentsError = error.message || "Не удалось удалить документ";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.deleteBusyId = "";
      }
    },
    async searchRetrieval() {
      const epoch = captureSessionEpoch();
      const query = this.retrievalQuery.trim();
      if (!query) {
        this.retrievalError = "Введите запрос";
        return;
      }

      this.retrievalBusy = true;
      this.retrievalError = "";
      this.retrievalAppliedProfile = "";
      this.retrievalEngineProfile = "";
      this.retrievalAnswerProfile = "";
      this.retrievalAnswer = "";
      this.retrievalAnswerCitations = [];

      try {
        const response = await apiFetch("/api/retrieval/search", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query,
            limit: 8,
            profile: this.retrievalProfile,
          }),
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.retrievalResults = payload.results;
        this.retrievalAppliedProfile = payload.retrieval_profile || "";
        this.retrievalEngineProfile = payload.profile || "";
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.retrievalError = error.message || "Не удалось выполнить поиск";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.retrievalBusy = false;
      }
    },
    async answerRetrieval() {
      const epoch = captureSessionEpoch();
      const query = this.retrievalQuery.trim();
      if (!query) {
        this.retrievalError = "Введите запрос";
        return;
      }

      this.retrievalAnswerBusy = true;
      this.retrievalError = "";
      this.retrievalAnswerProfile = "";
      this.retrievalAnswer = "";
      this.retrievalAnswerCitations = [];

      try {
        const response = await apiFetch("/api/retrieval/answer", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query,
            limit: 8,
            profile: this.retrievalProfile,
          }),
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.retrievalResults = payload.results || [];
        this.retrievalAppliedProfile = payload.retrieval_profile || "";
        this.retrievalEngineProfile = payload.retrieval_engine_profile || "";
        this.retrievalAnswerProfile = payload.answer_profile || "";
        this.retrievalAnswer = payload.answer || "";
        this.retrievalAnswerCitations = payload.citations || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.retrievalError = error.message || "Не удалось сформировать ответ";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.retrievalAnswerBusy = false;
      }
    },
    async runDocumentRetrieval(document) {
      if (!this.documentCanRunRetrieval(document)) {
        this.retrievalError = "Документ ещё не готов к проверке выдачи";
        return;
      }

      this.retrievalQuery = document.title;
      this.retrievalProfile = "hybrid_balanced";
      await this.searchRetrieval();
      this.$refs.retrievalPanel?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async fetchEvaluationRuns() {
      const epoch = captureSessionEpoch();
      this.evaluationRunsLoading = true;
      this.evaluationRunsError = "";

      try {
        const response = await apiFetch("/api/evaluation/runs", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationRuns = payload.runs || [];
        this.syncSelectedEvaluationRun();
        this.syncEvaluationComparison();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationRunsError = error.message || "Не удалось загрузить evaluation";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationRunsLoading = false;
      }
    },
    async fetchEvaluationDatasets() {
      const epoch = captureSessionEpoch();
      this.evaluationDatasetsLoading = true;
      this.evaluationDatasetsError = "";

      try {
        const response = await apiFetch(evaluationDatasetsEndpoint(), {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasets = payload.datasets || [];
        if (
          this.selectedEvaluationDatasetId &&
          !this.evaluationDatasets.some(
            (dataset) => dataset.id === this.selectedEvaluationDatasetId,
          )
        ) {
          this.selectedEvaluationDatasetId = "";
          this.evaluationDatasetCases = [];
          this.syncSelectedEvaluationDatasetForm();
        }
        this.syncSelectedEvaluationDatasetForm();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetsError = error.message || "Не удалось загрузить eval-наборы";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetsLoading = false;
      }
    },
    async createEvaluationDataset() {
      const epoch = captureSessionEpoch();
      this.evaluationDatasetCreateBusy = true;
      this.evaluationDatasetsError = "";

      try {
        const response = await apiFetch(evaluationDatasetsEndpoint(), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: this.evaluationDatasetName,
            version: this.evaluationDatasetVersion || "v1",
          }),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const dataset = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetName = "";
        this.evaluationDatasetVersion = "v1";
        await this.fetchEvaluationDatasets();
        // Session могла смениться во время вложенного refresh: старое действие
        // не восстанавливает selection и не запускает follow-up в новой сессии.
        if (!isSessionEpochCurrent(epoch)) return;
        this.selectedEvaluationDatasetId = dataset.id;
        this.syncSelectedEvaluationDatasetForm();
        await this.fetchEvaluationDatasetCases(dataset.id);
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetsError = error.message || "Не удалось создать eval-набор";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetCreateBusy = false;
      }
    },
    async selectEvaluationDataset(dataset) {
      this.selectedEvaluationDatasetId = dataset?.id || "";
      this.evaluationDatasetCases = [];
      this.resetEvaluationCaseForm();
      this.syncSelectedEvaluationDatasetForm();
      if (this.selectedEvaluationDatasetId) {
        await this.fetchEvaluationDatasetCases(this.selectedEvaluationDatasetId);
      }
    },
    selectedEvaluationDataset() {
      return (
        this.evaluationDatasets.find(
          (dataset) => dataset.id === this.selectedEvaluationDatasetId,
        ) || null
      );
    },
    syncSelectedEvaluationDatasetForm() {
      const dataset = this.selectedEvaluationDataset();
      this.evaluationDatasetEditName = dataset?.name || "";
      this.evaluationDatasetEditVersion = dataset?.version || "";
    },
    async updateSelectedEvaluationDataset() {
      const epoch = captureSessionEpoch();
      if (!this.selectedEvaluationDatasetId) {
        return;
      }
      this.evaluationDatasetUpdateBusy = true;
      this.evaluationDatasetsError = "";

      try {
        const response = await apiFetch(evaluationDatasetEndpoint(this.selectedEvaluationDatasetId), {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: this.evaluationDatasetEditName,
            version: this.evaluationDatasetEditVersion,
          }),
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        await this.fetchEvaluationDatasets();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetsError = error.message || "Не удалось сохранить eval-набор";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetUpdateBusy = false;
      }
    },
    async deleteSelectedEvaluationDataset() {
      const epoch = captureSessionEpoch();
      if (!this.selectedEvaluationDatasetId) {
        return;
      }
      const datasetId = this.selectedEvaluationDatasetId;
      this.evaluationDatasetDeleteBusyId = datasetId;
      this.evaluationDatasetsError = "";

      try {
        const response = await apiFetch(evaluationDatasetEndpoint(datasetId), {
          method: "DELETE",
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        this.selectedEvaluationDatasetId = "";
        this.evaluationDatasetCases = [];
        this.resetEvaluationCaseForm();
        await this.fetchEvaluationDatasets();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetsError = error.message || "Не удалось удалить eval-набор";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetDeleteBusyId = "";
      }
    },
    async fetchEvaluationDatasetCases(datasetId) {
      const epoch = captureSessionEpoch();
      this.evaluationDatasetCasesLoading = true;
      this.evaluationDatasetCasesError = "";

      try {
        const response = await apiFetch(evaluationDatasetCasesEndpoint(datasetId), {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetCases = payload.cases || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetCasesError = error.message || "Не удалось загрузить кейсы набора";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetCasesLoading = false;
      }
    },
    async createEvaluationDatasetCase() {
      const epoch = captureSessionEpoch();
      if (!this.selectedEvaluationDatasetId) {
        return;
      }
      this.evaluationDatasetCaseCreateBusy = true;
      this.evaluationDatasetCasesError = "";

      try {
        const expectedSources = parseExpectedSourcesJson(this.evaluationCaseExpectedSourcesJson);
        this.evaluationCaseExpectedSources = expectedSources;
        const endpoint = this.editingEvaluationCaseId
          ? evaluationDatasetCaseEndpoint(
              this.selectedEvaluationDatasetId,
              this.editingEvaluationCaseId,
            )
          : evaluationDatasetCasesEndpoint(this.selectedEvaluationDatasetId);
        const response = await apiFetch(endpoint, {
            method: this.editingEvaluationCaseId ? "PUT" : "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              question: this.evaluationCaseQuestion,
              expected_sources: expectedSources,
              reference_text: this.evaluationCaseReferenceText,
              must_include: commaList(this.evaluationCaseMustInclude),
              must_not_include: commaList(this.evaluationCaseMustNotInclude),
              max_results: Number(this.evaluationCaseMaxResults || 8),
              retrieval_profile: this.evaluationCaseProfile || null,
              require_citations: true,
            }),
          });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        this.resetEvaluationCaseForm();
        await this.fetchEvaluationDatasetCases(this.selectedEvaluationDatasetId);
        // Session могла смениться во вложенном refresh кейсов: старое действие
        // не запускает следующий refresh наборов в новой сессии.
        if (!isSessionEpochCurrent(epoch)) return;
        await this.fetchEvaluationDatasets();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetCasesError = error.message || "Не удалось сохранить кейс";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetCaseCreateBusy = false;
      }
    },
    editEvaluationDatasetCase(item) {
      this.editingEvaluationCaseId = item.id;
      this.evaluationCaseQuestion = item.question || "";
      this.evaluationCaseReferenceText = item.expected_answer?.reference_text || "";
      this.evaluationCaseExpectedSources = Array.isArray(item.expected_sources)
        ? item.expected_sources
        : [];
      this.syncEvaluationCaseExpectedSourcesJson();
      this.evaluationCaseMustInclude = (item.expected_answer?.must_include || []).join(", ");
      this.evaluationCaseMustNotInclude = (item.expected_answer?.must_not_include || []).join(", ");
      this.evaluationCaseMaxResults = item.max_results || 8;
      this.evaluationCaseProfile = item.retrieval_profile || "hybrid_balanced";
    },
    resetEvaluationCaseForm() {
      this.editingEvaluationCaseId = "";
      this.evaluationCaseQuestion = "";
      this.evaluationCaseReferenceText = "";
      this.evaluationCaseExpectedSources = [];
      this.evaluationCaseExpectedSourcesJson = "";
      this.evaluationCaseMustInclude = "";
      this.evaluationCaseMustNotInclude = "";
      this.evaluationCaseMaxResults = 8;
      this.evaluationCaseProfile = "hybrid_balanced";
    },
    syncEvaluationCaseExpectedSourcesJson() {
      this.evaluationCaseExpectedSourcesJson = formatExpectedSourcesJson(
        this.evaluationCaseExpectedSources,
      );
    },
    syncEvaluationCaseExpectedSourcesFromJson() {
      try {
        this.evaluationCaseExpectedSources = parseExpectedSourcesJson(
          this.evaluationCaseExpectedSourcesJson,
        );
        this.evaluationDatasetCasesError = "";
      } catch (error) {
        this.evaluationDatasetCasesError = error.message || "Некорректные ожидаемые источники";
      }
    },
    addExpectedSourceFromRetrieval(result) {
      this.evaluationCaseExpectedSources = addExpectedSource(
        this.evaluationCaseExpectedSources,
        expectedSourceFromRetrievalResult(result),
      );
      this.syncEvaluationCaseExpectedSourcesJson();
    },
    addExpectedSourceFromChunk(chunk) {
      this.evaluationCaseExpectedSources = addExpectedSource(
        this.evaluationCaseExpectedSources,
        expectedSourceFromDocumentChunk(this.selectedDocumentId, chunk),
      );
      this.syncEvaluationCaseExpectedSourcesJson();
    },
    removeEvaluationExpectedSource(index) {
      this.evaluationCaseExpectedSources = removeExpectedSource(
        this.evaluationCaseExpectedSources,
        index,
      );
      this.syncEvaluationCaseExpectedSourcesJson();
    },
    expectedSourceSummary(source) {
      return expectedSourceSummaryValue(source);
    },
    async deleteEvaluationDatasetCase(item) {
      const epoch = captureSessionEpoch();
      if (!this.selectedEvaluationDatasetId || !item?.id) {
        return;
      }
      this.evaluationDatasetCaseDeleteBusyId = item.id;
      this.evaluationDatasetCasesError = "";

      try {
        const response = await apiFetch(
          evaluationDatasetCaseEndpoint(this.selectedEvaluationDatasetId, item.id),
          { method: "DELETE" },
        );
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        if (this.editingEvaluationCaseId === item.id) {
          this.resetEvaluationCaseForm();
        }
        await this.fetchEvaluationDatasetCases(this.selectedEvaluationDatasetId);
        // Session могла смениться во вложенном refresh кейсов: старое действие
        // не запускает следующий refresh наборов в новой сессии.
        if (!isSessionEpochCurrent(epoch)) return;
        await this.fetchEvaluationDatasets();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationDatasetCasesError = error.message || "Не удалось удалить кейс";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationDatasetCaseDeleteBusyId = "";
      }
    },
    async runSelectedEvaluationDataset() {
      const epoch = captureSessionEpoch();
      if (!this.selectedEvaluationDatasetId) {
        return;
      }
      this.evaluationRunBusy = true;
      this.evaluationRunsError = "";

      try {
        const response = await apiFetch(evaluationDatasetRunEndpoint(this.selectedEvaluationDatasetId), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: communitySmokeRequestBody(this.evaluationDocumentIds),
        });
        const payloadText = await response.text();
        const payload = payloadText ? parseJsonOrNull(payloadText) : null;
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(payload?.error_message || payloadText);
        }

        await this.fetchEvaluationRuns();
        if (!isSessionEpochCurrent(epoch)) return;
        const createdRun =
          this.evaluationRuns.find((run) => run.id === payload?.id) || payload;
        if (createdRun?.id) {
          await this.openEvaluationCases(createdRun);
        }
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        await this.fetchEvaluationRuns();
        // Session могла смениться во вложенном refresh прогонов: ошибка прежней
        // сессии не публикуется в новую session/UI.
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationRunsError = error.message || "Не удалось запустить eval-набор";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationRunBusy = false;
      }
    },
    async runCommunitySmokeEvaluation() {
      await this.runCommunitySmokeEvaluationWithDocuments(this.evaluationDocumentIds);
    },
    async repeatEvaluationRun(run) {
      await this.runCommunitySmokeEvaluationWithDocuments(evaluationRunDocumentIds(run));
    },
    async runCommunitySmokeEvaluationWithDocuments(documentIds) {
      const epoch = captureSessionEpoch();
      this.evaluationRunBusy = true;
      this.evaluationRunsError = "";

      try {
        const response = await apiFetch(communitySmokeEndpoint(), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: communitySmokeRequestBody(documentIds),
        });
        const payloadText = await response.text();
        const payload = payloadText ? parseJsonOrNull(payloadText) : null;
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(payload?.error_message || payloadText);
        }

        await this.fetchEvaluationRuns();
        if (!isSessionEpochCurrent(epoch)) return;
        const createdRun =
          this.evaluationRuns.find((run) => run.id === payload?.id) || payload;
        if (createdRun?.id) {
          await this.openEvaluationCases(createdRun);
        }
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        await this.fetchEvaluationRuns();
        // Session могла смениться во вложенном refresh прогонов: ошибка прежней
        // сессии не публикуется в новую session/UI.
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationRunsError = error.message || "Не удалось запустить smoke evaluation";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationRunBusy = false;
      }
    },
    evaluationDocuments() {
      return this.documents.filter((document) => this.documentCanRunRetrieval(document));
    },
    isEvaluationDocumentSelected(document) {
      return this.evaluationDocumentIds.includes(document.id);
    },
    toggleEvaluationDocument(document) {
      if (this.isEvaluationDocumentSelected(document)) {
        this.evaluationDocumentIds = this.evaluationDocumentIds.filter(
          (documentId) => documentId !== document.id,
        );
        return;
      }
      this.evaluationDocumentIds = [...this.evaluationDocumentIds, document.id];
    },
    selectAllEvaluationDocuments() {
      this.evaluationDocumentIds = this.evaluationDocuments().map((document) => document.id);
    },
    clearEvaluationDocuments() {
      this.evaluationDocumentIds = [];
    },
    evaluationDocumentSummary() {
      const selectedCount = this.evaluationDocumentIds.length;
      if (selectedCount === 0) {
        return "Все готовые документы";
      }
      return `${selectedCount} выбрано`;
    },
    evaluationDocumentScopeLabel(run) {
      return evaluationDocumentScopeLabelValue(run);
    },
    async fetchRetrievalRuns() {
      const epoch = captureSessionEpoch();
      this.retrievalRunsLoading = true;
      this.retrievalRunsError = "";

      try {
        const response = await apiFetch("/api/retrieval/runs?limit=50", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.retrievalRuns = payload.runs || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.retrievalRunsError = error.message || "Не удалось загрузить запуски retrieval";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.retrievalRunsLoading = false;
      }
    },
    async openEvaluationCases(run) {
      const epoch = captureSessionEpoch();
      this.selectedEvaluationRunId = run.id;
      this.selectedEvaluationRunName = `${run.dataset_name} ${run.dataset_version}`;
      this.evaluationCases = [];
      this.externalRagasRuns = [];
      this.selectedExternalRagasRunId = "";
      this.externalRagasCompareBaseId = "";
      this.externalRagasCompareCandidateId = "";
      this.evaluationCasesLoading = true;
      this.evaluationCasesError = "";

      try {
        const response = await apiFetch(`/api/evaluation/runs/${encodeURIComponent(run.id)}/cases`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationCases = payload.cases || [];
        await this.fetchExternalRagasRuns(run.id);
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationCasesError = error.message || "Не удалось загрузить кейсы evaluation";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationCasesLoading = false;
      }
    },
    async fetchExternalRagasRuns(runId = this.selectedEvaluationRunId) {
      const epoch = captureSessionEpoch();
      if (!runId) {
        return;
      }

      this.externalRagasRunsLoading = true;
      this.externalRagasRunsError = "";

      try {
        const response = await apiFetch(externalRagasRunsEndpoint(runId), {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.externalRagasRuns = payload.runs || [];
        this.syncSelectedExternalRagasRun();
        this.syncExternalRagasComparison();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.externalRagasRunsError =
          error.message || "Не удалось загрузить внешние RAGAS-прогоны";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.externalRagasRunsLoading = false;
      }
    },
    async importExternalRagasRun(event) {
      const epoch = captureSessionEpoch();
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !this.selectedEvaluationRunId) {
        return;
      }

      this.externalRagasImportBusy = true;
      this.externalRagasRunsError = "";

      try {
        const raw = JSON.parse(await file.text());
        // Session могла смениться во время awaited File.text: старое действие
        // не отправляет следующий POST в новую сессию.
        if (!isSessionEpochCurrent(epoch)) return;
        const payload = normalizeExternalRagasImportPayload({
          ...raw,
          source_file_name: raw.source_file_name || file.name,
          source_imported_by: raw.source_imported_by || "community",
        });
        const response = await apiFetch(externalRagasRunsEndpoint(this.selectedEvaluationRunId), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const createdRun = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.selectedExternalRagasRunId = createdRun?.id || "";
        this.externalRagasCompareCandidateId = createdRun?.id || "";
        await this.fetchExternalRagasRuns();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.externalRagasRunsError =
          error.message || "Не удалось импортировать внешний RAGAS-прогон";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.externalRagasImportBusy = false;
      }
    },
    async deleteExternalRagasRun(run) {
      const epoch = captureSessionEpoch();
      if (!run?.id || !this.selectedEvaluationRunId) {
        return;
      }
      if (!window.confirm(`Удалить внешний RAGAS-прогон "${run.runner} ${run.model}"?`)) {
        return;
      }

      this.externalRagasDeleteBusyId = run.id;
      this.externalRagasRunsError = "";

      try {
        const response = await apiFetch(
          externalRagasRunEndpoint(this.selectedEvaluationRunId, run.id),
          {
            method: "DELETE",
            headers: { Accept: "application/json" },
          },
        );
        if (!response.ok) {
          throw new Error(await response.text());
        }
        if (!isSessionEpochCurrent(epoch)) return;
        this.externalRagasRuns = this.externalRagasRuns.filter((item) => item.id !== run.id);
        if (this.externalRagasJsonRunId === run.id) {
          this.externalRagasJsonRunId = "";
        }
        if (this.selectedExternalRagasRunId === run.id) {
          this.selectedExternalRagasRunId = "";
        }
        this.syncSelectedExternalRagasRun();
        this.syncExternalRagasComparison();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.externalRagasRunsError =
          error.message || "Не удалось удалить внешний RAGAS-прогон";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.externalRagasDeleteBusyId = "";
      }
    },
    async downloadEvaluationRagasExport(run) {
      const epoch = captureSessionEpoch();
      if (!this.canDownloadRagasExport(run)) {
        this.evaluationRunsError = "RAGAS export доступен только для завершенных запусков";
        return;
      }

      this.evaluationExportBusyId = run.id;
      this.evaluationRunsError = "";

      try {
        const response = await apiFetch(ragasExportEndpoint(run.id), {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const cases = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        downloadJson(ragasExportFileName(run), cases);
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.evaluationRunsError = error.message || "Не удалось скачать RAGAS export";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.evaluationExportBusyId = "";
      }
    },
    async fetchAgents() {
      const epoch = captureSessionEpoch();
      this.agentsLoading = true;
      this.agentsError = "";

      try {
        const response = await apiFetch("/api/agents", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.agents = payload.agents || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentsError = error.message || "Не удалось загрузить агентов";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentsLoading = false;
      }
    },
    resetAgentForm() {
      this.agentName = "";
      this.agentDescription = "";
      this.agentRetrievalProfile = "hybrid_balanced";
      this.agentCreateError = "";
    },
    async createAgent() {
      const epoch = captureSessionEpoch();
      const name = this.agentName.trim();
      if (!name) {
        this.agentCreateError = "Введите название агента";
        return;
      }

      this.agentCreateBusy = true;
      this.agentCreateError = "";

      try {
        const response = await apiFetch("/api/agents", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            description: this.agentDescription.trim() || null,
            retrieval_profile: this.agentRetrievalProfile,
            active: true,
          }),
        });
        if (!isSessionEpochCurrent(epoch)) return;
        if (!response.ok) {
          throw new Error(await response.text());
        }
        await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.resetAgentForm();
        this.agentCreateOpen = false;
        await this.fetchAgents();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentCreateError = error.message || "Не удалось создать агента";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentCreateBusy = false;
      }
    },
    async openAgentDocuments(agent) {
      const epoch = captureSessionEpoch();
      this.selectedAgentDocumentsId = agent.id;
      this.selectedAgentDocumentsName = agent.name;
      this.agentDocumentIds = [];
      this.agentDocumentsLoading = true;
      this.agentDocumentsError = "";

      try {
        const response = await apiFetch(`/api/agents/${encodeURIComponent(agent.id)}/documents`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentDocumentIds = payload.document_ids || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentDocumentsError = error.message || "Не удалось загрузить документы агента";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentDocumentsLoading = false;
      }
    },
    closeAgentDocuments() {
      this.selectedAgentDocumentsId = "";
      this.selectedAgentDocumentsName = "";
      this.agentDocumentIds = [];
      this.agentDocumentsError = "";
      this.agentDocumentsLoading = false;
      this.agentDocumentsSaving = false;
    },
    isAgentDocumentSelected(documentId) {
      return this.agentDocumentIds.includes(documentId);
    },
    toggleAgentDocument(documentId) {
      if (this.isAgentDocumentSelected(documentId)) {
        this.agentDocumentIds = this.agentDocumentIds.filter((id) => id !== documentId);
        return;
      }
      this.agentDocumentIds = [...this.agentDocumentIds, documentId];
    },
    async saveAgentDocuments() {
      const epoch = captureSessionEpoch();
      if (!this.selectedAgentDocumentsId) {
        return;
      }

      this.agentDocumentsSaving = true;
      this.agentDocumentsError = "";

      try {
        const response = await apiFetch(`/api/agents/${encodeURIComponent(this.selectedAgentDocumentsId)}/documents`, {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            document_ids: this.agentDocumentIds,
          }),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentDocumentIds = payload.document_ids || [];
        await this.fetchAgents();
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentDocumentsError = error.message || "Не удалось сохранить документы агента";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentDocumentsSaving = false;
      }
    },
    async updateAgentProfile(agent, retrievalProfile) {
      const epoch = captureSessionEpoch();
      if (!agent || agent.retrieval_profile === retrievalProfile) {
        return;
      }

      const previousProfile = agent.retrieval_profile;
      agent.retrieval_profile = retrievalProfile;
      this.agentProfileBusyId = agent.id;
      this.agentProfileError = "";

      try {
        const response = await apiFetch(`/api/agents/${encodeURIComponent(agent.id)}/retrieval-profile`, {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            retrieval_profile: retrievalProfile,
          }),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const updated = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        Object.assign(agent, updated);
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        agent.retrieval_profile = previousProfile;
        this.agentProfileError = error.message || "Не удалось сохранить профиль агента";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentProfileBusyId = "";
      }
    },
    async openAgentTest(agent) {
      const epoch = captureSessionEpoch();
      this.selectedAgentTestId = agent.id;
      this.selectedAgentTestName = agent.name;
      this.agentTestProfile = agent.retrieval_profile;
      this.agentTestDocumentIds = [];
      this.agentTestResults = [];
      this.agentTestError = "";
      this.agentTestLoading = true;

      try {
        const response = await apiFetch(`/api/agents/${encodeURIComponent(agent.id)}/documents`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentTestDocumentIds = payload.document_ids || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentTestError = error.message || "Не удалось загрузить документы агента";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentTestLoading = false;
      }
    },
    closeAgentTest() {
      this.selectedAgentTestId = "";
      this.selectedAgentTestName = "";
      this.agentTestProfile = "";
      this.agentTestDocumentIds = [];
      this.agentTestQuery = "";
      this.agentTestResults = [];
      this.agentTestError = "";
      this.agentTestBusy = false;
      this.agentTestLoading = false;
    },
    async runAgentTest() {
      const epoch = captureSessionEpoch();
      const query = this.agentTestQuery.trim();
      if (!query) {
        this.agentTestError = "Введите запрос";
        return;
      }
      if (this.agentTestDocumentIds.length === 0) {
        this.agentTestError = "Сначала привяжите документы к агенту";
        return;
      }

      this.agentTestBusy = true;
      this.agentTestError = "";
      this.agentTestResults = [];

      try {
        const response = await apiFetch("/api/retrieval/search", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query,
            limit: 8,
            profile: this.agentTestProfile,
            document_ids: this.agentTestDocumentIds,
          }),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentTestResults = payload.results || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.agentTestError = error.message || "Не удалось выполнить запрос агента";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.agentTestBusy = false;
      }
    },
    async fetchLogs() {
      const epoch = captureSessionEpoch();
      this.logsLoading = true;
      this.logsError = "";

      try {
        const params = new URLSearchParams({ limit: "50" });
        if (this.logEventType.trim()) {
          params.set("event_type", this.logEventType.trim());
        }
        const response = await apiFetch(`/api/logs?${params.toString()}`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.logs = payload.events || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.logsError = error.message || "Не удалось загрузить логи";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.logsLoading = false;
      }
    },
    async fetchImportJobs() {
      const epoch = captureSessionEpoch();
      this.importJobsLoading = true;
      this.importJobsError = "";

      try {
        const response = await apiFetch("/api/document-import-jobs?limit=50", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const payload = await response.json();
        if (!isSessionEpochCurrent(epoch)) return;
        this.importJobs = payload.jobs || [];
      } catch (error) {
        if (!isSessionEpochCurrent(epoch)) return;
        this.importJobsError = error.message || "Не удалось загрузить запуски обработки";
      } finally {
        if (isSessionEpochCurrent(epoch)) this.importJobsLoading = false;
      }
    },
    closeEvaluationCases() {
      this.selectedEvaluationRunId = "";
      this.selectedEvaluationRunName = "";
      this.evaluationCases = [];
      this.evaluationCasesError = "";
      this.evaluationCasesLoading = false;
      this.externalRagasRuns = [];
      this.externalRagasRunsError = "";
      this.externalRagasRunsLoading = false;
      this.externalRagasImportBusy = false;
      this.externalRagasJsonRunId = "";
      this.externalRagasDeleteBusyId = "";
      this.externalRagasFilter = "";
      this.externalRagasSort = "date_desc";
      this.externalRagasBestMetric = "faithfulness";
      this.selectedExternalRagasRunId = "";
      this.externalRagasCompareBaseId = "";
      this.externalRagasCompareCandidateId = "";
    },
    syncSelectedEvaluationRun() {
      if (!this.selectedEvaluationRunId) {
        return;
      }
      const selected = this.evaluationRuns.find((run) => run.id === this.selectedEvaluationRunId);
      if (!selected) {
        this.closeEvaluationCases();
      } else {
        this.selectedEvaluationRunName = `${selected.dataset_name} ${selected.dataset_version}`;
      }
    },
    completedEvaluationRuns() {
      return this.filteredEvaluationRuns().filter((run) => run.status === "completed");
    },
    filteredEvaluationRuns() {
      return filterEvaluationRuns(this.evaluationRuns, {
        status: this.evaluationStatusFilter,
        profile: this.evaluationProfileFilter,
      });
    },
    groupedEvaluationRuns() {
      return groupEvaluationRuns(this.filteredEvaluationRuns());
    },
    syncEvaluationComparison() {
      const completed = this.completedEvaluationRuns();
      const ids = new Set(completed.map((run) => run.id));
      if (!ids.has(this.evaluationCompareBaseId)) {
        this.evaluationCompareBaseId = completed[1]?.id || completed[0]?.id || "";
      }
      if (
        !ids.has(this.evaluationCompareCandidateId) ||
        this.evaluationCompareCandidateId === this.evaluationCompareBaseId
      ) {
        this.evaluationCompareCandidateId =
          completed.find((run) => run.id !== this.evaluationCompareBaseId)?.id || "";
      }
    },
    evaluationCompareBaseRun() {
      return this.evaluationRuns.find((run) => run.id === this.evaluationCompareBaseId) || null;
    },
    evaluationCompareCandidateRun() {
      return this.evaluationRuns.find((run) => run.id === this.evaluationCompareCandidateId) || null;
    },
    selectedEvaluationRun() {
      return this.evaluationRuns.find((run) => run.id === this.selectedEvaluationRunId) || null;
    },
    selectedEvaluationRunDocuments() {
      const documentIds = this.selectedEvaluationRun()?.document_ids || [];
      return documentIds.map((documentId) => ({
        id: documentId,
        document: this.documents.find((document) => document.id === documentId) || null,
      }));
    },
    evaluationRunDocumentTitle(item) {
      return item?.document?.title || item?.id || "Документ недоступен";
    },
    evaluationRunDocumentMeta(item) {
      const document = item?.document;
      if (!document) {
        return "нет в текущем списке документов";
      }
      return `${document.chunk_count || 0} фрагментов · ${this.statusLabel(document.status)}`;
    },
    hasEvaluationComparison() {
      return Boolean(this.evaluationCompareBaseRun() && this.evaluationCompareCandidateRun());
    },
    evaluationComparisonDelta(key) {
      return evaluationMetricDelta(
        this.evaluationCompareBaseRun(),
        this.evaluationCompareCandidateRun(),
        key,
      );
    },
    evaluationComparisonTone(key) {
      return evaluationMetricDeltaTone(this.evaluationComparisonDelta(key));
    },
    syncSelectedExternalRagasRun() {
      const selected = selectedExternalRagasRunValue(
        this.externalRagasRuns,
        this.selectedExternalRagasRunId,
      );
      if (selected) {
        return;
      }
      this.selectedExternalRagasRunId =
        sortExternalRagasRuns(this.externalRagasRuns, "date_desc")[0]?.id || "";
    },
    selectedExternalRagasRun() {
      return selectedExternalRagasRunValue(
        this.externalRagasRuns,
        this.selectedExternalRagasRunId,
      );
    },
    externalRagasSelectionOptions() {
      return sortExternalRagasRuns(this.externalRagasRuns, "date_desc");
    },
    externalRagasRunOptionLabel(run) {
      const date = run?.source_imported_at || run?.created_at;
      const formattedDate = date ? this.formatDate(date) : this.t("format.date.none");
      return `${run?.runner || "RAGAS"} / ${run?.model || "-"} / ${formattedDate}`;
    },
    syncExternalRagasComparison() {
      const options = this.externalRagasSelectionOptions();
      const ids = new Set(options.map((run) => run.id));
      if (options.length < 2) {
        this.externalRagasCompareBaseId = "";
        this.externalRagasCompareCandidateId = "";
        return;
      }
      if (!ids.has(this.externalRagasCompareCandidateId)) {
        this.externalRagasCompareCandidateId = options[0]?.id || "";
      }
      if (
        !ids.has(this.externalRagasCompareBaseId) ||
        this.externalRagasCompareBaseId === this.externalRagasCompareCandidateId
      ) {
        this.externalRagasCompareBaseId =
          options.find((run) => run.id !== this.externalRagasCompareCandidateId)?.id || "";
      }
      if (this.externalRagasCompareBaseId === this.externalRagasCompareCandidateId) {
        this.externalRagasCompareCandidateId =
          options.find((run) => run.id !== this.externalRagasCompareBaseId)?.id || "";
      }
    },
    externalRagasCompareBaseRun() {
      return selectedExternalRagasRunValue(
        this.externalRagasRuns,
        this.externalRagasCompareBaseId,
      );
    },
    externalRagasCompareCandidateRun() {
      return selectedExternalRagasRunValue(
        this.externalRagasRuns,
        this.externalRagasCompareCandidateId,
      );
    },
    hasExternalRagasRunComparison() {
      return Boolean(
        this.externalRagasCompareBaseRun() &&
          this.externalRagasCompareCandidateRun() &&
          this.externalRagasCompareBaseId !== this.externalRagasCompareCandidateId,
      );
    },
    externalRagasRunMetricDelta(key) {
      return externalRagasRunMetricDeltaValue(
        this.externalRagasCompareBaseRun(),
        this.externalRagasCompareCandidateRun(),
        key,
      );
    },
    externalRagasRunMetricTone(key) {
      return evaluationMetricDeltaTone(this.externalRagasRunMetricDelta(key));
    },
    filteredExternalRagasRuns() {
      return sortExternalRagasRuns(
        filterExternalRagasRuns(this.externalRagasRuns, this.externalRagasFilter),
        this.externalRagasSort,
      );
    },
    bestExternalRagasRun() {
      return bestExternalRagasRunValue(this.externalRagasRuns, this.externalRagasBestMetric);
    },
    isBestExternalRagasRun(run) {
      return Boolean(run?.id && run.id === this.bestExternalRagasRun()?.id);
    },
    hasExternalRagasMetricComparison() {
      return Boolean(this.selectedEvaluationRun() && this.selectedExternalRagasRun());
    },
    externalRagasRunUsesCaseIds() {
      return externalRagasRunUsesCaseIdsValue(this.selectedExternalRagasRun());
    },
    toggleExternalRagasJson(run) {
      this.externalRagasJsonRunId = this.externalRagasJsonRunId === run.id ? "" : run.id;
    },
    externalRagasRunJsonPayload(run) {
      return externalRagasRunJsonPayloadValue(run);
    },
    downloadExternalRagasRunJson(run) {
      downloadJson(externalRagasRunFileName(run), this.externalRagasRunJsonPayload(run));
    },
    externalRagasMetricDelta(key) {
      return externalRagasMetricDeltaValue(
        this.selectedEvaluationRun(),
        this.selectedExternalRagasRun(),
        key,
      );
    },
    externalRagasMetricTone(key) {
      return evaluationMetricDeltaTone(this.externalRagasMetricDelta(key));
    },
    hasExternalRagasCaseMetrics(caseResult, index) {
      return Boolean(
        externalRagasCaseResultValue(this.selectedExternalRagasRun(), caseResult, index),
      );
    },
    externalRagasCaseMetric(caseResult, index, key) {
      return externalRagasCaseMetricValue(
        this.selectedExternalRagasRun(),
        caseResult,
        index,
        key,
      );
    },
    externalRagasCaseMetricDelta(caseResult, index, key) {
      return externalRagasCaseMetricDeltaValue(
        caseResult,
        this.selectedExternalRagasRun(),
        index,
        key,
      );
    },
    externalRagasCaseMetricTone(caseResult, index, key) {
      return evaluationMetricDeltaTone(
        this.externalRagasCaseMetricDelta(caseResult, index, key),
      );
    },
    statusLabel(status) {
      return {
        registered: "Зарегистрирован",
        processing: "Обрабатывается",
        completed: "Готов",
        failed: "Ошибка",
      }[status || "registered"];
    },
    statusTone(status) {
      return {
        registered: "status-waiting",
        processing: "status-processing",
        completed: "status-ready",
        failed: "status-failed",
      }[status || "registered"];
    },
    documentTypeLabel(document) {
      return documentType(document).label;
    },
    documentTypeClass(document) {
      return `badge badge-${documentType(document).tone}`;
    },
    documentStatusClass(status) {
      return `status-badge ${documentStatusTone(status)}`;
    },
    /**
     * AC-1: technical-роль применяется только к реальному хэшу/ревизии.
     * Fallback «без хеша» — обычный UI-текст (Inter), не technical mono.
     */
    documentSubtitleTechnical(document) {
      return Boolean(document?.content_hash || document?.current_version_label);
    },
    documentSubtitle(document) {
      if (document?.content_hash) {
        return String(document.content_hash).slice(0, 12);
      }
      return document?.current_version_label || "без хеша";
    },
    evaluationStatusLabel(status) {
      return {
        running: "Выполняется",
        completed: "Готов",
        failed: "Ошибка",
      }[status || "running"];
    },
    evaluationStatusTone(status) {
      return {
        running: "status-processing",
        completed: "status-ready",
        failed: "status-failed",
      }[status || "running"];
    },
    processingStepLabel(state) {
      return {
        active: "В работе",
        completed: "Готово",
        failed: "Ошибка",
        ready: "Доступно",
        waiting: "Ожидает",
      }[state || "waiting"];
    },
    // Story 1.7 / FR-4: форматы дат/чисел/размеров идут через текущий locale
    // (ru-RU/en-US), а не через жёстко заданный ru-RU.
    formatBytes(value) {
      const size = Number(value) || 0;
      if (!size) return this.t("format.bytes.empty");
      if (size < 1024) return `${this.formatNumber(size)} ${this.t("format.bytes.unit.b")}`;
      if (size < 1024 * 1024) {
        return `${this.formatNumber(size / 1024, BYTES_FRACTION_OPTIONS)} ${this.t("format.bytes.unit.kb")}`;
      }
      return `${this.formatNumber(size / 1024 / 1024, BYTES_FRACTION_OPTIONS)} ${this.t("format.bytes.unit.mb")}`;
    },
    formatDate(value, options) {
      return formatLocaleDate(this.locale, value, options);
    },
    formatScore(value) {
      return Number(value || 0).toFixed(3);
    },
    formatMetric(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return Number(value).toFixed(3);
    },
    formatMetricDelta(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "-";
      }
      const formatted = Math.abs(Number(value)).toFixed(3);
      if (Math.abs(Number(value)) < 0.0005) {
        return "0.000";
      }
      return `${Number(value) > 0 ? "+" : "-"}${formatted}`;
    },
    aggregateMetric(run, key) {
      return runAggregateMetric(run, key);
    },
    answerMetric(caseResult, key) {
      return answerMetricValue(caseResult, key);
    },
    hasAnswerMetrics(caseResult) {
      return caseHasAnswerMetrics(caseResult);
    },
    canDownloadRagasExport(run) {
      return canDownloadRagasExportValue(run);
    },
    answerEvaluationMethod(caseResult) {
      return answerEvaluationMethodValue(caseResult);
    },
    answerEvaluationMethodLabel(method) {
      return answerEvaluationMethodLabelValue(method);
    },
    externalRagasMetric(run, key) {
      return externalRagasMetricValue(run, key);
    },
    profileLabel(value) {
      return (
        this.retrievalProfiles.find((profile) => profile.value === value)?.label ||
        value ||
        "unknown"
      );
    },
    formatJson(value) {
      return JSON.stringify(value, null, 2);
    },
  };
};

window.Alpine = Alpine;
Alpine.start();

function parseJsonOrNull(value) {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function commaList(value) {
  return String(value || "")
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function documentType(document) {
  const mediaType = String(document?.media_type || "").toLowerCase();
  const title = String(document?.title || "").toLowerCase();
  const source = `${mediaType} ${title}`;

  if (source.includes("pdf")) return { label: "PDF", tone: "pdf" };
  if (source.includes("spreadsheet") || source.includes("excel") || source.includes("xlsx")) {
    return { label: "XLSX", tone: "xlsx" };
  }
  if (source.includes("wordprocessing") || source.includes("msword") || source.includes("docx")) {
    return { label: "DOCX", tone: "docx" };
  }
  if (source.includes("presentation") || source.includes("powerpoint") || source.includes("pptx")) {
    return { label: "PPTX", tone: "pptx" };
  }
  if (source.includes("markdown") || source.endsWith(".md")) return { label: "MD", tone: "md" };
  if (source.includes("json")) return { label: "JSON", tone: "json" };
  if (source.includes("yaml") || source.includes("yml")) return { label: "YAML", tone: "yaml" };
  if (source.includes("text") || source.endsWith(".txt")) return { label: "TXT", tone: "txt" };

  return { label: "FILE", tone: "file" };
}

function documentStatusTone(status) {
  return {
    registered: "status-inactive",
    processing: "status-draft",
    completed: "status-active",
    failed: "status-error",
  }[status || "registered"];
}

function downloadJson(fileName, value) {
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
